import './RichTextEditor.scss';

/**
 * @file RichTextEditor.js
 * @description Rich text editor component using TipTap with various formatting options.
 */

import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import classNames from 'classnames';
import { ListItem } from '@tiptap/extension-list';
import { Popover, Divider } from '@mui/material';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import Highlight from '@tiptap/extension-highlight';
import { Dropdown } from '../helpers/Dropdown';
import { DOMSerializer } from '@tiptap/pm/model';
import {
    useContext,
    useEffect,
    useState,
    useRef,
    useLayoutEffect,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { ChromePicker } from 'react-color';
import EmojiPicker from 'emoji-picker-react';
import Emoji from '../../utils/tiptap/EmojiExtension';
import ResizableImage, {
    EDITOR_IMAGE_RESIZE_OPTIONS,
    parseImageMarginValue,
    parsePixelWidth,
} from '../../utils/tiptap/ResizableImageExtension';
import SSODropdown from '../shared/asyncDropdowns/SSODropdown';
import { ROUTES } from '../../constants/routes';
import {
    buildAbsoluteSSOLinkUrl,
    getSSOLinkId,
} from '../../utils/resolveSSOLinks';
import { buildExternalAppUrl } from '../../utils/externalAppLinks';
import AssetGridView, {
    renderAssetPlaceholder,
} from '../assets/components/AssetGridView';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { InputField, SwitchField } from '../helpers/FormComponents';
import TagInput from '../helpers/TagInput';
import Tooltip from '../ui/tooltips/Tooltip';
import { FileDrop } from '../helpers/FileDrop';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import {
    createAsset,
    fetchAssetByID,
    fetchAssets,
    getClientOptions,
    queryPlanTypes,
} from '../../services/clientService';
import {
    getDataPointCategories,
    getDataPointSubCategories,
} from '../../services/individualService';
import { uploadFile } from '../../services/containerService';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
    sanitizeFileName,
} from '../helpers/FileUtils';
import Toast from '../ui/toasts/Toast';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { fetchNotificationTokens } from '../../services/systemService';
import style from '../assets/AssetLibrary.module.scss';
import { VideoNode } from './VideoNode';
import { QRCodeNode } from './QRCodeNode';
import ReactDOM from 'react-dom';
import DivParagraph from './DivParagraph';
import { queryPortalPages } from '../../services/portalPageService';
import { PortalLinkNode } from './PortalLinkNode';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { ClientOptionEnum } from '../../utils/data/enums';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getText } from '../../i18n';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import { TenantTiers } from '../../utils/data/enums';

const DEFAULT_MENU_ROUTE_OPTIONS = [
    { label: getText('msg_20e338624cee'), value: ROUTES.MY_ASSETS },
    { label: getText('msg_927f40673764'), value: ROUTES.MY_BENEFITS },
    { label: getText('msg_f1702b468627'), value: ROUTES.MY_MESSAGES },
];

// Reuses the Push "Home" label so the Menu dropdown reads consistently across channels.
const HOME_MENU_ROUTE_OPTION = {
    label: getText('msg_70f8bb9a8a53'),
    value: ROUTES.ROOT,
};

const FILTER_OPTIONS_ASSET_LIBRARY = [
    { id: '5', name: 'Video' },
    { id: '6', name: 'Audio' },
    { id: '7', name: 'Image' },
    { id: '8', name: 'Document' },
    { id: '9', name: 'Spreadsheet' },
    { id: '1', name: 'Content Only' },
    { id: '4', name: 'Shared' },
    { id: '2', name: 'Unassigned' },
    { id: '13', name: 'Global' },
    { id: '14', name: 'Uploaded' },
];

const overlayBackground = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    zIndex: 999,
    overflow: 'auto',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255, 255, 255, 0.56)',
    display: 'flex',
    padding: '20px',
    justifyContent: 'center',
};

const overlayContainer = {
    width: '620px',
    height: '740px',
    maxHeight: '100hv',
    overflow: 'auto',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    padding: '20px',
};

// Synthetic option that stands in for every DataPoint* token; the Return dropdown picks the real one.
const DATA_POINT_TOKEN_NAME = 'DataPoint';

// Presentation rules the notification token API does not describe.
const TOKEN_UI_RULES = {
    // The backend calculates these against the current quarter/year and ignores any date range.
    DataPointQTD: { disableAttributes: ['DateFrom', 'DateTo'] },
    DataPointYTD: { disableAttributes: ['DateFrom', 'DateTo'] },
    // Collected through its own popup instead of the generic attribute form.
    IndivRegLink: { usesDedicatedPopup: true },
};

const DATE_TOKEN_ATTRIBUTE_NAMES = ['Date', 'DateFrom', 'DateTo'];

function getTokenName(tokenValue) {
    if (typeof tokenValue !== 'string') {
        return '';
    }

    return tokenValue.replace(/^\[/, '').replace(/\]$/, '');
}

function isDataPointReturnTokenName(tokenName) {
    return (
        typeof tokenName === 'string' &&
        tokenName.startsWith(DATA_POINT_TOKEN_NAME) &&
        tokenName !== DATA_POINT_TOKEN_NAME
    );
}

function getDisabledTokenAttributeNames(baseTokenName) {
    return TOKEN_UI_RULES[baseTokenName]?.disableAttributes || [];
}

function tokenUsesAttributeForm(tokenOption) {
    if (TOKEN_UI_RULES[getTokenName(tokenOption?.value)]?.usesDedicatedPopup) {
        return false;
    }

    return (tokenOption?.attributes || []).length > 0;
}

function buildTokenTag(tokenName, parameters) {
    const attributes = Object.entries(parameters)
        .filter(
            ([, value]) =>
                value !== null && value !== undefined && value !== '',
        )
        .map(
            ([name, value]) =>
                `${name}="${String(value).replace(/"/g, '&quot;')}"`,
        );

    return attributes.length > 0
        ? `[${tokenName} ${attributes.join(' ')}]`
        : `[${tokenName}]`;
}

function parseTokenParameterDate(value) {
    return value ? new Date(`${value}T00:00:00`) : null;
}

function isSSOLink(url) {
    return getSSOLinkId(url) !== null;
}

const MENU_ROUTE_PATHS = [
    ROUTES.MY_ASSETS,
    ROUTES.MY_BENEFITS,
    ROUTES.MY_MESSAGES,
];

// Menu links are stored relative for in-app use but absolute once they have to survive outside the app.
function getMenuRoutePath(url) {
    if (typeof url !== 'string' || !url) {
        return null;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);
        if (parsedUrl.origin !== window.location.origin) {
            return null;
        }

        return MENU_ROUTE_PATHS.includes(parsedUrl.pathname)
            ? parsedUrl.pathname
            : null;
    } catch (error) {
        return null;
    }
}

function isMenuLink(url) {
    return getMenuRoutePath(url) !== null;
}

// The Home link is kept separate from the generic menu routes so it can be enabled
// independently. /my-home is the Individual-side entry point used outside the app.
function isHomeLink(url) {
    if (typeof url !== 'string' || !url) {
        return false;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);
        return (
            parsedUrl.origin === window.location.origin &&
            [ROUTES.ROOT, ROUTES.MY_HOME].includes(parsedUrl.pathname)
        );
    } catch (error) {
        return false;
    }
}

function buildAbsoluteAppUrl(routePath) {
    return buildExternalAppUrl(routePath);
}

function buildPortalPageUrl(portalPageId) {
    return buildAbsoluteAppUrl(`${ROUTES.PORTAL_PAGES}/${portalPageId}`);
}

// Keeps the in-app markers on the anchor so readers still get SPA navigation and access checks.
function toAbsoluteAppLinks(html) {
    if (typeof html !== 'string' || !html) {
        return html || '';
    }

    const root = new DOMParser().parseFromString(html, 'text/html').body;

    root.querySelectorAll('span[data-portal-link="true"]').forEach(span => {
        const portalPageId = span.getAttribute('data-portal-id');
        if (!portalPageId) {
            return;
        }

        const anchor = document.createElement('a');
        anchor.setAttribute('href', buildPortalPageUrl(portalPageId));
        anchor.setAttribute('data-portal-link', 'true');
        anchor.setAttribute('data-portal-id', portalPageId);
        anchor.setAttribute('target', '_self');
        anchor.innerHTML = span.innerHTML;
        span.replaceWith(anchor);
    });

    root.querySelectorAll('a[href]').forEach(anchor => {
        const href = anchor.getAttribute('href');
        const ssoLinkId = getSSOLinkId(href);
        if (ssoLinkId) {
            anchor.setAttribute('href', buildAbsoluteSSOLinkUrl(ssoLinkId));
            return;
        }

        const menuRoutePath = getMenuRoutePath(href);
        if (menuRoutePath) {
            anchor.setAttribute('href', buildAbsoluteAppUrl(menuRoutePath));
            return;
        }

        if (isHomeLink(href)) {
            anchor.setAttribute('href', buildAbsoluteAppUrl(ROUTES.MY_HOME));
        }
    });

    return root.innerHTML;
}

function isAssetLinkAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

function buildEditorFeaturePolicy({
    disableFontStyling,
    disableClearFormatting,
    disableClipboard,
    disableHistory,
    disableParagraphStyling,
    disableEmoji,
    disableAllLinks,
    disableWebsiteLink,
    disablePortalLink,
    disableMenu,
    disableSSO,
    disableAssetLink,
    disableHomeLink,
    disablePhone,
    disableImage,
    disableVideo,
    disableQRCode,
    disableToken,
    disableTable,
    plainTextLinksOnly,
}) {
    return {
        fontStyling: !disableFontStyling,
        clearFormatting: !disableClearFormatting,
        clipboard: !disableClipboard,
        history: !disableHistory,
        paragraphStyling: !disableParagraphStyling,
        emoji: !disableEmoji,

        links: {
            website: !(disableAllLinks || disableWebsiteLink),
            portal: !(disableAllLinks || disablePortalLink),
            menu: !(disableAllLinks || disableMenu),
            sso: !(disableAllLinks || disableSSO),
            asset: !(disableAllLinks || disableAssetLink),
            home: !(disableAllLinks || disableHomeLink),
        },
        plainTextLinks: Boolean(plainTextLinksOnly),

        phone: !disablePhone,
        image: !disableImage,
        video: !disableVideo,
        qrCode: !disableQRCode,
        token: !disableToken,
        table: !disableTable,
    };
}

const FONT_STYLING_ATTRS = ['color', 'face', 'size'];
const FONT_STYLING_PROPS = [
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'text-decoration',
    'color',
    'background-color',
];
const PARAGRAPH_STYLING_PROPS = ['text-align'];

const PASTE_UNWRAP_RULES = [
    {
        flag: 'fontStyling',
        selector: 'b, strong, i, em, u, s, strike, del, mark, font',
    },
    { flag: 'paragraphStyling', selector: 'ul, ol, li' },
];

const PASTE_REMOVE_RULES = [
    { flag: 'image', selector: 'img' },
    { flag: 'video', selector: 'video' },
    { flag: 'qrCode', selector: 'qr-code' },
    { flag: 'table', selector: 'table' },
];

function classifyLink(element) {
    if (element.getAttribute('data-portal-link') === 'true') return 'portal';
    if (element.getAttribute('data-link-type') === 'asset') return 'asset';
    const href = element.getAttribute('href') || '';
    if (href.startsWith('tel:')) return 'phone';
    if (isSSOLink(href)) return 'sso';
    if (isHomeLink(href)) return 'home';
    if (isMenuLink(href)) return 'menu';
    return 'website';
}

function isLinkAllowed(type, features) {
    if (type === 'phone') return features.phone;
    return features.links[type] ?? false;
}

function stripStyleProps(element, props) {
    for (const prop of props) {
        element.style.removeProperty(prop);
    }
    if (!element.getAttribute('style')) {
        element.removeAttribute('style');
    }
}

function flattenLinksToPlainText(root) {
    root.querySelectorAll('[data-portal-link="true"]').forEach(el => {
        const portalPageId = el.getAttribute('data-portal-id');
        el.replaceWith(portalPageId ? buildPortalPageUrl(portalPageId) : '');
    });

    root.querySelectorAll('a').forEach(el => {
        const href = el.getAttribute('href');
        const ssoLinkId = getSSOLinkId(href);
        el.replaceWith(
            ssoLinkId
                ? buildAbsoluteSSOLinkUrl(ssoLinkId)
                : href || el.textContent || '',
        );
    });
}

function sanitizePastedHtml(html, features) {
    if (typeof html !== 'string' || !html) return html || '';

    const root = new DOMParser().parseFromString(html, 'text/html').body;

    for (const { flag, selector } of PASTE_UNWRAP_RULES) {
        if (features[flag]) continue;
        root.querySelectorAll(selector).forEach(el =>
            el.replaceWith(...Array.from(el.childNodes)),
        );
    }

    if (!features.fontStyling) {
        root.querySelectorAll('*').forEach(el => {
            for (const attr of FONT_STYLING_ATTRS) el.removeAttribute(attr);
            stripStyleProps(el, FONT_STYLING_PROPS);
        });
    }

    if (!features.paragraphStyling) {
        root.querySelectorAll('*').forEach(el => {
            el.removeAttribute('align');
            stripStyleProps(el, PARAGRAPH_STYLING_PROPS);
        });
    }

    root.querySelectorAll('a, [data-portal-link="true"]').forEach(el => {
        if (!isLinkAllowed(classifyLink(el), features)) {
            el.replaceWith(...Array.from(el.childNodes));
        }
    });

    if (features.plainTextLinks) {
        flattenLinksToPlainText(root);
    }

    for (const { flag, selector } of PASTE_REMOVE_RULES) {
        if (features[flag]) continue;
        root.querySelectorAll(selector).forEach(el => el.remove());
    }

    return root.innerHTML;
}

const RichTextEditor = ({
    initialContent,
    onContentUpdate,
    disableFontStyling = false,
    disableClearFormatting = false,
    disableClipboard = false,
    disableHistory = false,
    disableParagraphStyling = false,
    disableEmoji = false,
    disableAllLinks = false,
    disableWebsiteLink = false,
    disablePortalLink = false,
    disableMenu = false,
    disableSSO = false,
    disableAssetLink = false,
    // Home rides along with Menu by default since it lives in the Menu dropdown; override to enable it independently.
    disableHomeLink = disableMenu,
    disablePhone = false,
    disableImage = false,
    disableVideo = false,
    disableQRCode = false,
    disableToken = false,
    disableTable = false,
    plainTextLinksOnly = false,
    useAbsoluteLinks = false,
    agencyId = null,

    onAssetDelete,
    portalId = null,
    isPreview = false,
    isViewLive = false,
    placeholderText,
}) => {
    const features = buildEditorFeaturePolicy({
        disableFontStyling,
        disableClearFormatting,
        disableClipboard,
        disableHistory,
        disableParagraphStyling,
        disableEmoji,

        disableAllLinks,
        disableWebsiteLink,
        disablePortalLink,
        disableMenu,
        disableSSO,
        disableAssetLink,
        disableHomeLink,

        disablePhone,
        disableImage,
        disableVideo,
        disableQRCode,
        disableToken,
        disableTable,
        plainTextLinksOnly,
    });

    const { showModal } = useContext(ModalContext);
    const { clientTierMeetsRequirement, user } = useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const isSSODisabled =
        disableSSO || !clientTierMeetsRequirement(TenantTiers.Bronze);
    const [formData, setFormData] = useState();
    const [plainText, setPlainText] = useState();

    const [file, setFile] = useState(null);
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [textAssets, setTextAssets] = useState([]);
    const [text, setText] = useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState([]);
    const [assets, setAssets] = useState([]);
    const [isAssetsLoading, setIsAssetsLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showItalicTooltip, setShowItalicTooltip] = useState(false);
    const [showUnderlineTooltip, setShowUnderlineTooltip] = useState(false);
    const [showStrikeTooltip, setShowStrikeTooltip] = useState(false);
    const [showHighlightTooltip, setShowHighlightTooltip] = useState(false);
    const [showEraserTooltip, setshowEraserTooltip] = useState(false);
    const [showTextColorTooltip, setShowTextColorTooltip] = useState(false);
    const [showTextTooltip, setShowTextTooltip] = useState(false);
    const [showEditTooltip, setShowEditTooltip] = useState(false);
    const [showParagraphTooltip, setShowParagraphTooltip] = useState(false);
    const [showEmojiTooltip, setShowEmojiTooltip] = useState(false);
    const [showAddTooltip, setShowAddTooltip] = useState(false);
    const [showTableTooltip, setShowTableTooltip] = useState(false);
    const [showCutTooltip, setShowCutTooltip] = useState(false);
    const [showCopyTooltip, setShowCopyTooltip] = useState(false);
    const [showPasteTooltip, setShowPasteTooltip] = useState(false);
    const [showUndoTooltip, setShowUndoTooltip] = useState(false);
    const [showRedoTooltip, setShowRedoTooltip] = useState(false);
    const [showAlignLeftTooltip, setShowAlignLeftTooltip] = useState(false);
    const [showAlignCenterTooltip, setShowAlignCenterTooltip] = useState(false);
    const [showAlignRightTooltip, setShowAlignRightTooltip] = useState(false);
    const [showJustifyTooltip, setShowJustifyTooltip] = useState(false);
    const [showNumberedListTooltip, setShowNumberedListTooltip] =
        useState(false);
    const [showBulletedListTooltip, setShowBulletedListTooltip] =
        useState(false);
    const [showSsoTooltip, setShowSsoTooltip] = useState(false);
    const [showLinkTooltip, setShowLinkTooltip] = useState(false);
    const [showPhoneTooltip, setShowPhoneTooltip] = useState(false);
    const [showImageTooltip, setShowImageTooltip] = useState(false);
    const [showVideoTooltip, setShowVideoTooltip] = useState(false);
    const [showQrTooltip, setShowQrTooltip] = useState(false);
    const [showTokenTooltip, setShowTokenTooltip] = useState(false);
    const [notificationTokenOptions, setNotificationTokenOptions] = useState(
        [],
    );
    const [selectedTokenValue, setSelectedTokenValue] = useState('');
    const [isTokenParameterStep, setIsTokenParameterStep] = useState(false);
    const [tokenAttributeValues, setTokenAttributeValues] = useState({});
    const [dataPointReturn, setDataPointReturn] = useState('');
    const [planTypeOptions, setPlanTypeOptions] = useState([]);
    const [dataPointCategories, setDataPointCategories] = useState([]);
    const [dataPointSubCategories, setDataPointSubCategories] = useState([]);

    const [showInsertTableTooltip, setShowInsertTableToolTip] = useState(false);
    const [showEditTableTooltip, setShowEditTableTooltip] = useState(false);
    const [showCellTableTooltip, setShowCellTableTooltip] = useState(false);

    const [isTableSelected, setIsTableSelected] = useState(false);
    const [selectedItem, setSelectedItem] = useState('text'); // Text Menu Item is selected by default
    const [selectedSubItem, setSelectedSubItem] = useState('');
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [popupContent, setPopupContent] = useState(null);
    const [token, setToken] = useState(''); // State for token
    const [displayTextColorPicker, setDisplayTextColorPicker] = useState(false);
    const [textColor, setTextColor] = useState({
        r: '251',
        g: '24',
        b: '3',
        a: '1',
    });
    const [displayHighlightColorPicker, setDisplayHighlightColorPicker] =
        useState(false);
    const [highlightColor, setHighlightColor] = useState({
        r: '251',
        g: '207',
        b: '0',
        a: '1',
    });
    const [displayCellColorPicker, setDisplayCellColorPicker] = useState(false);
    const [cellColor, setCellColor] = useState({
        r: '255',
        g: '255',
        b: '255',
        a: '255',
    });
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [selectedEmoji, setSelectedEmoji] = useState(null);
    const previewConfig = {
        defaultEmoji: '1f60a',
        defaultCaption: getText('msg_bc4a709a23f2'),
        showPreview: false,
    };

    const [isCellColorPopoverOpen, setIsCellColorPopoverOpen] = useState(false);
    const [anchorCC, setAnchorCC] = useState(null);
    const [prevContainerAssets, setPrevContainerAssets] = useState(textAssets);
    const fontSizeOptions = [
        {
            id: 1,
            label: getText('msg_768e0c1c6957'),
            size: '16pt',
            bold: true,
        },
        {
            id: 2,
            label: getText('msg_31341c6f0c7a'),
            size: '14pt',
            bold: true,
        },
        {
            id: 3,
            label: getText('msg_718a7e8a582e'),
            size: '12pt',
            bold: false,
        },
        {
            id: 4,
            label: getText('msg_aeecd6a62c9c'),
            size: '12pt',
            bold: false,
            color: {
                hex: '#667085',
                rgb: {
                    r: '102',
                    g: '112',
                    b: '133',
                    a: '1',
                },
            },
        },
        { id: 5, label: getText('msg_1ff7e21c72c5'), size: '8pt', bold: false },
        {
            id: 6,
            label: getText('msg_a4e4aab6a18a'),
            size: '10pt',
            bold: false,
        },
        {
            id: 7,
            label: getText('msg_1648962325ed'),
            size: '12pt',
            bold: false,
        },
        {
            id: 8,
            label: getText('msg_669bd9cb5b06'),
            size: '14pt',
            bold: false,
        },
        {
            id: 9,
            label: getText('msg_c1d4a3d96484'),
            size: '16pt',
            bold: false,
        },
        {
            id: 10,
            label: getText('msg_0bf870a25fb1'),
            size: '18pt',
            bold: false,
        },
        {
            id: 11,
            label: getText('msg_8007e833c684'),
            size: '20pt',
            bold: false,
        },
        {
            id: 12,
            label: getText('msg_cc7ddfac0497'),
            size: '22pt',
            bold: false,
        },
        {
            id: 13,
            label: getText('msg_25488b57de32'),
            size: '24pt',
            bold: false,
        },
    ];
    const hasInitializedRef = useRef(false);
    const debouncedContentUpdateRef = useRef(null);
    const isSettingInitialContentRef = useRef(false);
    const assetLibraryAutoOpenedRef = useRef(false);

    // helpers: normalize various color inputs to lowercase 6-digit hex (#rrggbb)
    function normalizeHex(input) {
        if (input === undefined || input === null) return null;
        const s = String(input).trim();

        // hex formats: "#667085", "667085", "#678", "678"
        const hexOnly = s.replace(/^#/, '');
        if (/^[0-9a-f]{6}$/i.test(hexOnly)) return '#' + hexOnly.toLowerCase();
        if (/^[0-9a-f]{3}$/i.test(hexOnly)) {
            // expand shorthand e.g. "abc" -> "aabbcc"
            const [r, g, b] = hexOnly.split('');
            return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
        }

        // rgb(...) or rgba(...) like "rgb(102,112,133)" or "rgba(102,112,133,1)"
        const rgbMatch = s.match(
            /rgba?\s*$$\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})/i,
        );
        if (rgbMatch) {
            const r = Math.max(0, Math.min(255, Number(rgbMatch[1]) || 0));
            const g = Math.max(0, Math.min(255, Number(rgbMatch[2]) || 0));
            const b = Math.max(0, Math.min(255, Number(rgbMatch[3]) || 0));
            const toHex = n => n.toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
        }

        return null;
    }

    // extract normalized hex from option.color (prefers .hex, falls back to .rgb object)
    function optionColorHex(opt) {
        if (!opt || !opt.color) return null;
        const c = opt.color;

        if (typeof c === 'string') return normalizeHex(c);

        if (c && typeof c.hex === 'string' && c.hex.trim() !== '') {
            return normalizeHex(c.hex);
        }

        // rgb object: { r: '102', g: '112', b: '133', a: '1' } - accepts strings or numbers
        if (c.rgb && (c.rgb.r !== undefined || c.rgb.R !== undefined)) {
            const r = Number(c.rgb.r ?? c.rgb.R ?? 0);
            const g = Number(c.rgb.g ?? c.rgb.G ?? 0);
            const b = Number(c.rgb.b ?? c.rgb.B ?? 0);
            return normalizeHex(`rgb(${r},${g},${b})`);
        }

        return null;
    }

    // Main function (no-arg style, uses outer-scope editor & fontSizeOptions)
    function getDefaultFontSizeSize() {
        // Defensive checks
        if (
            !editor ||
            typeof editor.getAttributes !== 'function' ||
            !Array.isArray(fontSizeOptions)
        ) {
            return '12pt';
        }

        const textStyleAttrs = editor.getAttributes('textStyle') || {};
        const rawFontSize = textStyleAttrs.fontSize;

        if (
            rawFontSize === undefined ||
            rawFontSize === null ||
            String(rawFontSize).trim() === ''
        ) {
            return '12pt';
        }

        const fontSize = String(rawFontSize).trim();
        const isBold =
            typeof editor.isActive === 'function'
                ? Boolean(editor.isActive('bold'))
                : false;

        // normalize editor color if present
        const editorRawColor = textStyleAttrs.color;
        const editorColorHex = normalizeHex(editorRawColor); // may be null

        // 1) size + bold + color (only if editor provided color)
        if (editorColorHex) {
            const colorMatch = fontSizeOptions.find(
                opt =>
                    String(opt?.size).trim() === fontSize &&
                    Boolean(opt?.bold) === isBold &&
                    optionColorHex(opt) === editorColorHex,
            );
            if (colorMatch) return colorMatch.label;
        }

        // 2) size + bold
        const exactMatch = fontSizeOptions.find(
            opt =>
                String(opt?.size).trim() === fontSize &&
                Boolean(opt?.bold) === isBold,
        );
        if (exactMatch) return exactMatch.label;

        // 3) size-only
        const sizeOnlyMatch = fontSizeOptions.find(
            opt => String(opt?.size).trim() === fontSize,
        );
        if (sizeOnlyMatch) return sizeOnlyMatch.label;

        // 4) return raw fontSize string
        if (fontSize) return fontSize;

        // 5) final fallback
        return '12pt';
    }

    const handleFontSizeChange = option => {
        const fontSizeOption = fontSizeOptions.find(o => o.id === option);
        if (!fontSizeOption) {
            console.warn('Unknown font size id:', option);
            return;
        }

        setFontSize(fontSizeOption.size);

        const isBold =
            typeof editor.isActive === 'function'
                ? Boolean(editor.isActive('bold'))
                : false;
        if (fontSizeOption.bold !== isBold)
            editor.chain().focus().toggleBold().run();

        if (fontSizeOption.color) {
            setTextColor(fontSizeOption.color.rgb);
            editor.chain().focus().setColor(fontSizeOption.color.hex).run();
        }
    };

    const [linkType, setLinkType] = useState(null); // 'Website', 'Portal', 'SSO', 'Menu', 'Asset'
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [portalList, setPortalList] = useState([]);
    const [selectedPortal, setSelectedPortal] = useState(null);
    const [selectedAssetLink, setSelectedAssetLink] = useState(null);
    const [ssoId, setSsoId] = useState(null);
    const [linkDisplayText, setLinkDisplayText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [menuRouteOptions, setMenuRouteOptions] = useState(
        DEFAULT_MENU_ROUTE_OPTIONS,
    );
    // Notifications-only channels unlock just the Home destination until full menu deep-linking ships.
    const activeMenuRouteOptions = [
        ...(features.links.home ? [HOME_MENU_ROUTE_OPTION] : []),
        ...(features.links.menu ? menuRouteOptions : []),
    ];

    const getAssetExtension = assetLink => {
        if (!assetLink) return '';
        const typeFromField = assetLink.fileType || '';
        if (typeFromField) return String(typeFromField).toUpperCase();

        const url = assetLink.url || '';
        const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
        return match ? String(match[1]).toUpperCase() : '';
    };

    const renderAssetLinkSelection = () => {
        if (!selectedAssetLink) {
            return null;
        }

        const assetName = selectedAssetLink.name || 'Asset';
        const assetExtension = getAssetExtension(selectedAssetLink);

        return (
            <div className="rte-asset-link-section">
                <div className="rte-asset-preview-section">
                    <div className="rte-asset-preview-card">
                        {renderAssetPlaceholder(
                            selectedAssetLink.fileType,
                            selectedAssetLink.url,
                            assetName,
                        )}
                    </div>

                    <div className="rte-asset-description">
                        <div className="rte-asset-file-meta">
                            <div className="rte-asset-file-label">
                                {getText('msg_10e7ac23a20a')}
                            </div>
                            <div className="rte-asset-file-name">
                                {assetName}
                            </div>
                            {assetExtension && (
                                <div className="rte-asset-file-extension">
                                    {assetExtension}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="rte-asset-replace-button"
                            onClick={() =>
                                setIsAddFromAssetsChecked('assetLink')
                            }
                        >
                            <i className="bi bi-pencil" />
                            {getText('msg_a7cf7b25a703')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderDisplayTextInput = () => {
        if (plainTextLinksOnly) {
            return null;
        }

        return (
            <div className="rte-link-form-group">
                <div className="file-form-label">
                    {getText('msg_35209321d117')}
                    <span className="rte-required-asterisk">*</span>
                </div>
                <input
                    onChange={e => setLinkDisplayText(e.target.value)}
                    value={linkDisplayText}
                    className="form-control rte-link-input"
                    type="text"
                    placeholder={getText('msg_afbc6def06b9')}
                    aria-label={getText('msg_f640c574cde2')}
                />
            </div>
        );
    };

    const getEnabledLinkTypes = () => {
        const types = [];
        if (features.links.website) types.push('Website');
        if (features.links.portal) types.push('Portal');
        if (features.links.sso) types.push('SSO');
        if (features.links.menu || features.links.home) types.push('Menu');
        if (features.links.asset) types.push('Asset');
        return types;
    };
    useEffect(() => {
        const fetchMenuRouteLabels = async () => {
            try {
                const options = await getClientOptions(0, [
                    ClientOptionEnum.RenameBenefitsMenu,
                    ClientOptionEnum.RenameMessagesMenu,
                    ClientOptionEnum.RenameAssetsMenu,
                ]);

                const benefitsOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameBenefitsMenu,
                );
                const messagesOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameMessagesMenu,
                );
                const assetsOption = options.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.RenameAssetsMenu,
                );

                setMenuRouteOptions([
                    {
                        label:
                            assetsOption && !assetsOption.usingDefaultValue
                                ? assetsOption.value || 'Assets'
                                : 'Assets',
                        value: ROUTES.MY_ASSETS,
                    },
                    {
                        label:
                            benefitsOption && !benefitsOption.usingDefaultValue
                                ? benefitsOption.value || 'Benefits'
                                : 'Benefits',
                        value: ROUTES.MY_BENEFITS,
                    },
                    {
                        label:
                            messagesOption && !messagesOption.usingDefaultValue
                                ? messagesOption.value || 'Messages'
                                : 'Messages',
                        value: ROUTES.MY_MESSAGES,
                    },
                ]);
            } catch (error) {
                console.error('Failed to fetch menu route labels', error);
            }
        };

        fetchMenuRouteLabels();
    }, []);

    const popoverContentRef = useRef(null);
    const [popoverPaperStyle, setPopoverPaperStyle] = useState({});

    const [selectedPortalNodePos, setSelectedPortalNodePos] = useState(null);

    const navigate = useNavigate();

    const updatePopoverPosition = () => {
        if (!popoverContentRef.current || !anchorEl) return;

        const containerHeight = popoverContentRef.current.offsetHeight;
        const viewportHeight = window.innerHeight;
        const margin = 20; // margin from bottom of viewport

        let top = viewportHeight - containerHeight - margin;
        if (top < margin) top = margin;

        const anchorRect = anchorEl.getBoundingClientRect();
        const left = anchorRect.left;

        setPopoverPaperStyle({
            position: 'fixed',
            top,
            left,
            maxHeight: viewportHeight - margin * 2,
            overflowY: 'auto',
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            zIndex: 1300,
            minWidth: anchorRect.width,
        });
    };

    useLayoutEffect(() => {
        if (isPopupOpen) {
            updatePopoverPosition();
            window.addEventListener('resize', updatePopoverPosition);
        }
        return () => {
            window.removeEventListener('resize', updatePopoverPosition);
        };
    }, [isPopupOpen, anchorEl, popupContent]);

    useEffect(() => {
        if (!popoverContentRef.current || !isPopupOpen) return;

        const resizeObserver = new ResizeObserver(() => {
            updatePopoverPosition();
        });

        resizeObserver.observe(popoverContentRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isPopupOpen, popoverContentRef.current]);

    useLayoutEffect(() => {
        if (isPopupOpen) {
            updatePopoverPosition();
            window.addEventListener('resize', updatePopoverPosition);
        }
        return () => {
            window.removeEventListener('resize', updatePopoverPosition);
        };
    }, [isPopupOpen, anchorEl, popupContent]);

    useEffect(() => {
        if (!popoverContentRef.current || !isPopupOpen) return;

        const resizeObserver = new ResizeObserver(() => {
            updatePopoverPosition();
        });

        resizeObserver.observe(popoverContentRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isPopupOpen, popoverContentRef.current]);

    useEffect(() => {
        let isMounted = true;
        queryPortalPages(true)
            .then(payload => {
                if (!isMounted) return;
                const portalsArray = [];
                payload.viewModels.forEach(portalPage => {
                    if (portalPage.status === 1 && portalPage.id !== portalId) {
                        portalsArray.push({
                            value: portalPage.id,
                            label:
                                portalPage.name +
                                (portalPage.isChild === false ? ' (M)' : ''),
                        });
                    }
                });
                setPortalList(portalsArray);
            })
            .catch(err => {
                console.error('Failed to query portal pages', err);
            });

        return () => {
            isMounted = false;
        };
    }, [portalId]);

    const handleUploadFromDevice = type => {
        handleClose();
        setIsUploadFromDeviceChecked(type);
    };

    const handleAddFromAssets = type => {
        handleClose();
        setIsAddFromAssetsChecked(type);
    };

    const handleTextColorPickerToggle = () => {
        setDisplayTextColorPicker(!displayTextColorPicker);
    };

    const handleTextColorChange = (newColor, event) => {
        event.preventDefault();
        event.stopPropagation();
        setTextColor(newColor.rgb);
        editor.chain().focus().setColor(newColor.hex).run();
    };

    const handleHighlightColorPickerToggle = () => {
        setDisplayHighlightColorPicker(!displayHighlightColorPicker);
    };

    const handleHighlightColorChange = (newColor, event) => {
        event.preventDefault();
        event.stopPropagation();
        setHighlightColor(newColor.rgb);
        editor.chain().focus().toggleHighlight({ color: newColor.hex }).run();
    };

    const handleCellColorPickerToggle = event => {
        setDisplayCellColorPicker(!displayCellColorPicker);
    };

    const handleCellColorChange = (newColor, event) => {
        event.preventDefault();
        event.stopPropagation();
        setCellColor(newColor.rgb);
        editor
            .chain()
            .focus()
            .setCellAttribute('backgroundColor', newColor.hex)
            .run();
    };

    const handleEmojiSelect = emoji => {
        setSelectedEmoji(emoji);
        setIsEmojiPickerOpen(false);
        editor.chain().insertContent(emoji.emoji).run();
    };

    const setPhoneLink = (phoneNumber, displayText) => {
        if (!phoneNumber) return;

        const cleanPhone = phoneNumber.replace(/\D/g, '');
        editor
            .chain()
            .focus()
            .insertContent(
                `<a href="tel:${cleanPhone}">${displayText || phoneNumber}</a>`,
            )
            .run();

        setSelectedSubItem('');
        setAnchorEl(null);
        setIsPopupOpen(false);
    };

    const handlePhoneClick = event => {
        setSelectedSubItem('phone');

        let phoneNumber = '';
        let displayText = '';
        let content = null;

        content = (
            <div>
                <p className="modal-header-text">
                    {getText('msg_6f96b9381c59')}
                </p>
                <Divider sx={{ backgroundColor: 'gray' }} />
                <div className="modal-input-container">
                    {/* <div className="file-form-label">
                        Display Text
                        <span
                            style={{
                                color: '#EF4E45',
                                marginLeft: '4px',
                                fontWeight: '500',
                            }}
                        >
                            *
                        </span>
                    </div> */}
                    {/* <input
                        onChange={event => (displayText = event.target.value)}
                        defaultValue={displayText}
                        style={{
                            height: '48px',
                            borderRadius: '12px',
                            border: 'solid 1px rgba(47, 43, 67, 0.2)',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                        }}
                        className="form-control"
                        type="text"
                        placeholder="E.g.: Call Us"
                        aria-label="Enter display text"
                    ></input> */}
                </div>
                <div className="modal-input-container">
                    <div className="file-form-label">
                        {getText('msg_ab25d61bb124')}
                        <span className="modal-required-asterisk">*</span>
                    </div>
                    <input
                        onChange={event => (phoneNumber = event.target.value)}
                        defaultValue={phoneNumber}
                        className="form-control modal-text-input"
                        type="tel"
                        placeholder={getText('msg_29fe6efb98d4')}
                        aria-label={getText('msg_a22607853f3e')}
                    ></input>
                </div>
                <div
                    onClick={() => setPhoneLink(phoneNumber, displayText)}
                    type="button"
                    className="float-end button-base ing-button-primary-left modal-submit-button"
                >
                    {getText('msg_95802daab3a2')}
                </div>
            </div>
        );

        setPopupContent(content);
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    const setLink = (urlLink, displayTextLink, linkType = null) => {
        if (!editor) {
            console.warn('Editor not ready');
            return;
        }
        if (!urlLink || !displayTextLink) {
            editor.chain().focus().unsetLink().run();
        } else {
            const target =
                linkType === 'Portal' || linkType === 'Menu'
                    ? '_self'
                    : '_blank';
            const dataLinkTypeAttribute =
                linkType === 'Asset' ? ' data-link-type="asset"' : '';
            editor
                .chain()
                .focus()
                .insertContent(
                    `<a href="${urlLink}" target="${target}"${dataLinkTypeAttribute}>${displayTextLink}</a>`,
                )
                .run();
        }
        setSelectedSubItem('');
        setAnchorEl(null);
        setIsPopupOpen(false);
    };

    const updatePortalLinkDisplayText = (pos, portalId, newText) => {
        if (!editor || pos === null) return;

        const { state, view } = editor;
        const nodeType = state.schema.nodes.portalLink;
        if (!nodeType) return;

        const newNode = nodeType.create(
            { portalId: String(portalId) },
            state.schema.text(newText || ''),
        );

        const tr = state.tr.replaceWith(
            pos,
            pos + state.doc.nodeAt(pos).nodeSize,
            newNode,
        );
        view.dispatch(tr);
        editor.commands.focus();
    };

    const closeLinkPopup = () => {
        setSelectedSubItem('');
        setAnchorEl(null);
        setIsPopupOpen(false);
    };

    // Plain-text SSO links point at the app, which resolves the recipient's own login url on click.
    const getPlainTextLinkUrl = () => {
        if (linkType === 'Website') return linkUrl;
        if (linkType === 'Portal') {
            return selectedPortal
                ? buildPortalPageUrl(selectedPortal.value)
                : '';
        }
        if (linkType === 'Menu') {
            return selectedMenu ? buildAbsoluteAppUrl(selectedMenu.value) : '';
        }
        if (linkType === 'SSO') {
            return ssoId ? buildAbsoluteSSOLinkUrl(ssoId) : '';
        }
        if (linkType === 'Asset') return selectedAssetLink?.url || '';
        return '';
    };

    // Strips the link mark afterwards so the editor's autolink cannot turn the text back into an anchor.
    const insertPlainTextLink = urlText => {
        const insertPosition = editor.state.selection.from;

        editor
            .chain()
            .focus()
            .insertContent({ type: 'text', text: urlText })
            .setTextSelection({
                from: insertPosition,
                to: insertPosition + urlText.length,
            })
            .unsetLink()
            .setTextSelection(insertPosition + urlText.length)
            .run();

        closeLinkPopup();
    };

    const insertPortalLink = () => {
        if (selectedPortalNodePos !== null) {
            updatePortalLinkDisplayText(
                selectedPortalNodePos,
                selectedPortal.value,
                linkDisplayText,
            );
            closeLinkPopup();
            return;
        }

        editor.commands.focus();
        editor.commands.insertContent([
            {
                type: 'portalLink',
                attrs: { portalId: String(selectedPortal.value) },
                content: [{ type: 'text', text: linkDisplayText }],
            },
        ]);
        closeLinkPopup();
    };

    const insertConfiguredLink = () => {
        if (linkType === 'Asset' && !selectedAssetLink?.url) {
            Toast.error(
                getText('msg_cda753bde5d1'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        if (plainTextLinksOnly) {
            const plainTextUrl = getPlainTextLinkUrl();
            if (!plainTextUrl) {
                console.warn('Insert clicked but missing required data');
                return;
            }

            insertPlainTextLink(plainTextUrl);
            return;
        }

        if (linkType === 'Website') {
            setLink(linkUrl, linkDisplayText, 'Website');
            return;
        }

        if (linkType === 'Portal' && selectedPortal) {
            insertPortalLink();
            return;
        }

        if (linkType === 'SSO' && ssoId) {
            setLink(`https://ssoId:${ssoId}`, linkDisplayText, 'SSO');
            return;
        }

        if (linkType === 'Menu' && selectedMenu) {
            setLink(selectedMenu.value, linkDisplayText, 'Menu');
            return;
        }

        if (linkType === 'Asset') {
            setLink(selectedAssetLink.url, linkDisplayText, 'Asset');
            return;
        }

        console.warn('Insert clicked but missing required data');
    };

    const buildLinkPopupContent = currentLinkType => {
        const enabledLinkTypes = getEnabledLinkTypes();

        if (enabledLinkTypes.length === 0) {
            return null;
        }

        if (enabledLinkTypes.length === 1) {
            const singleType = enabledLinkTypes[0];
            if (linkType !== singleType) {
                setLinkType(singleType);
            }

            return (
                <div className="rte-link-popup">
                    <p className="rte-link-popup-title">
                        {getText('msg_07f5d328c4d1')}
                    </p>
                    <Divider className="rte-link-popup-divider" />

                    {singleType !== 'Asset' && renderDisplayTextInput()}

                    {/* Conditional Inputs Based on Single Link Type */}
                    {singleType === 'Website' && (
                        <div className="rte-link-form-group">
                            <div className="file-form-label">
                                {getText('msg_1cd49c79f9cf')}
                                <span className="rte-required-asterisk">*</span>
                            </div>
                            <input
                                onChange={e => setLinkUrl(e.target.value)}
                                value={linkUrl}
                                className="form-control rte-link-input"
                                type="text"
                                placeholder={getText('msg_ae5afe234b14')}
                                aria-label={getText('msg_ae5afe234b14')}
                            />
                        </div>
                    )}

                    {singleType === 'Portal' && (
                        <div className="rte-link-form-group">
                            <div className="file-form-label">
                                {getText('msg_3a3061182951')}
                                <span className="rte-required-asterisk">*</span>
                            </div>
                            <Dropdown
                                allowSearch={true}
                                options={portalList}
                                defaultValue={selectedPortal?.label || 'Select'}
                                onChange={value => {
                                    const portal = portalList.find(
                                        p => p.value === value,
                                    );
                                    setSelectedPortal(portal || null);
                                }}
                                maxOpenHeight={'200px'}
                                overlappingOptions={false}
                            />
                        </div>
                    )}

                    {singleType === 'SSO' && (
                        <div className="rte-link-form-group">
                            <div className="file-form-label">
                                {getText('msg_fb954a0b17de')}
                                <span className="rte-required-asterisk">*</span>
                            </div>
                            <SSODropdown
                                inlineLabel={false}
                                label=""
                                selectedSSO={ssoId || 0}
                                onSSOChange={id => setSsoId(id)}
                                client={true}
                                maxOpenHeight={'200px'}
                                overlappingOptions={false}
                            />
                        </div>
                    )}

                    {singleType === 'Asset' && (
                        <>
                            {renderAssetLinkSelection()}
                            {renderDisplayTextInput()}
                        </>
                    )}

                    {/* Insert Button */}
                    {(singleType !== 'Asset' || selectedAssetLink?.url) && (
                        <div className="rte-link-popup-footer">
                            <button
                                type="button"
                                className="button-base ing-button-primary-left rte-link-insert-button"
                                onClick={insertConfiguredLink}
                            >
                                {getText('msg_95802daab3a2')}
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        const dropdownOptions = enabledLinkTypes.map(type => ({
            label: type,
            value: type,
        }));

        let effectiveLinkType = currentLinkType;
        if (
            !effectiveLinkType ||
            !dropdownOptions.some(opt => opt.value === effectiveLinkType)
        ) {
            effectiveLinkType =
                dropdownOptions.length === 1 ? dropdownOptions[0].value : null;
            if (linkType !== effectiveLinkType) {
                setLinkType(effectiveLinkType);
            }
        }

        return (
            <div className="rte-link-popup">
                <p className="rte-link-popup-title">
                    {getText('msg_07f5d328c4d1')}
                </p>
                <Divider className="rte-link-popup-divider" />

                {/* Link Type Dropdown */}
                <div className="rte-link-form-group">
                    <div className="file-form-label">
                        {getText('msg_3deb74565196')}
                        <span className="rte-required-asterisk">*</span>
                    </div>
                    <Dropdown
                        defaultValue={effectiveLinkType || 'Select'}
                        options={dropdownOptions}
                        value={effectiveLinkType}
                        onChange={value => {
                            setLinkType(value);
                            setSelectedPortal(null);
                            setSsoId(null);
                            setSelectedMenu(null);
                            setSelectedAssetLink(null);
                            setLinkUrl('');
                        }}
                    />
                </div>

                {/* Display Text Input */}
                {effectiveLinkType !== 'select' &&
                    effectiveLinkType !== 'Asset' &&
                    renderDisplayTextInput()}

                {/* Conditional Inputs Based on Link Type */}
                {effectiveLinkType === 'Website' && (
                    <div className="rte-link-form-group">
                        <div className="file-form-label">
                            {getText('msg_1cd49c79f9cf')}
                            <span className="rte-required-asterisk">*</span>
                        </div>
                        <input
                            onChange={e => setLinkUrl(e.target.value)}
                            value={linkUrl}
                            className="form-control rte-link-input"
                            type="text"
                            placeholder={getText('msg_ae5afe234b14')}
                            aria-label={getText('msg_ae5afe234b14')}
                        />
                    </div>
                )}

                {effectiveLinkType === 'Portal' && (
                    <div className="rte-link-form-group">
                        <div className="file-form-label">
                            {getText('msg_3a3061182951')}
                            <span className="rte-required-asterisk">*</span>
                        </div>
                        <Dropdown
                            allowSearch={true}
                            options={portalList}
                            defaultValue={selectedPortal?.label || 'Select'}
                            onChange={value => {
                                const portal = portalList.find(
                                    p => p.value === value,
                                );
                                setSelectedPortal(portal || null);
                            }}
                            maxOpenHeight={'200px'}
                            overlappingOptions={false}
                        />
                    </div>
                )}

                {effectiveLinkType === 'SSO' && (
                    <div className="rte-link-form-group">
                        <div className="file-form-label">
                            {getText('msg_fb954a0b17de')}
                            <span className="rte-required-asterisk">*</span>
                        </div>
                        <SSODropdown
                            inlineLabel={false}
                            label=""
                            selectedSSO={ssoId || 0}
                            onSSOChange={id => setSsoId(id)}
                            client={true}
                            maxOpenHeight={'200px'}
                            overlappingOptions={false}
                        />
                    </div>
                )}

                {effectiveLinkType === 'Menu' && (
                    <div className="rte-link-form-group">
                        <div className="file-form-label">
                            {getText('msg_610e3667a84e')}
                            <span className="rte-required-asterisk">*</span>
                        </div>
                        <Dropdown
                            options={activeMenuRouteOptions}
                            defaultValue={selectedMenu?.label || 'Select'}
                            value={selectedMenu?.value || ''}
                            onChange={value => {
                                const menu = activeMenuRouteOptions.find(
                                    m => m.value === value,
                                );
                                setSelectedMenu(menu || null);
                            }}
                            maxOpenHeight={'200px'}
                            overlappingOptions={false}
                        />
                    </div>
                )}

                {effectiveLinkType === 'Asset' && (
                    <>
                        {renderAssetLinkSelection()}
                        {renderDisplayTextInput()}
                    </>
                )}

                {/* Insert Button */}
                {(effectiveLinkType !== 'Asset' || selectedAssetLink?.url) && (
                    <div className="rte-link-popup-footer">
                        <button
                            type="button"
                            className="button-base ing-button-primary-left rte-link-insert-button"
                            onClick={insertConfiguredLink}
                        >
                            {getText('msg_95802daab3a2')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const getSelectedPortalLink = () => {
        if (!editor) return null;
        const { state } = editor;
        const { selection } = state;
        const { from, to, $from } = selection;

        if (selection.node?.type?.name === 'portalLink') {
            return { portalId: selection.node.attrs.portalId, nodePos: from };
        }

        for (let d = $from.depth; d > 0; d--) {
            const n = $from.node(d);
            if (n.type.name === 'portalLink') {
                return {
                    portalId: n.attrs.portalId,
                    nodePos: $from.before(d),
                };
            }
        }

        let found = null;
        state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === 'portalLink') {
                found = { portalId: node.attrs.portalId, nodePos: pos };
                return false;
            }
            return true;
        });

        return found;
    };

    const handleLinkClick = event => {
        setSelectedSubItem('link');

        const enabledTypes = getEnabledLinkTypes();

        if (enabledTypes.length === 1) {
            setLinkType(enabledTypes[0]);
        } else {
            setLinkType(null);
        }

        setSelectedPortal(null);
        setSsoId(null);
        setSelectedMenu(null);
        setSelectedAssetLink(null);
        setSelectedPortalNodePos(null);
        setLinkDisplayText('');
        setLinkUrl('');

        let existingUrl = '';

        const portal = getSelectedPortalLink();
        setSelectedPortalNodePos(portal?.nodePos ?? null);
        if (portal?.portalId && features.links.portal) {
            setLinkType('Portal');
            const portalOption = portalList.find(
                p => String(p.value) === String(portal.portalId),
            );
            setSelectedPortal(portalOption || null);
            const { from, to } = editor.state.selection;
            let display = editor.state.doc.textBetween(from, to);
            if (!display && portal?.nodePos != null) {
                const node = editor.state.doc.nodeAt(portal.nodePos);
                if (node?.type?.name === 'portalLink') {
                    display = node?.textContent ?? '';
                }
            }
            setLinkDisplayText(display);
        } else {
            const html = getHTMLFromSelection();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const linkElement = doc.querySelector('a');
            existingUrl = linkElement ? linkElement.getAttribute('href') : '';
            const existingLinkType =
                linkElement?.getAttribute('data-link-type') || '';
            const existingText = getSelectedText();

            if (existingUrl) {
                const existingMenuRoutePath = getMenuRoutePath(existingUrl);
                const menuOption = menuRouteOptions.find(
                    m => m.value === existingMenuRoutePath,
                );

                const existingSSOLinkId = getSSOLinkId(existingUrl);
                const isExistingSSOLink = existingSSOLinkId !== null;

                if (menuOption) {
                    if (features.links.menu) {
                        setLinkType('Menu');
                        setSelectedMenu(menuOption);
                    } else {
                        setLinkType(null);
                    }
                } else if (isExistingSSOLink) {
                    if (features.links.sso) {
                        setLinkType('SSO');
                        setSsoId(existingSSOLinkId);
                    } else {
                        setLinkType(null);
                    }
                } else if (isHomeLink(existingUrl)) {
                    if (features.links.home) {
                        setLinkType('Menu');
                        setSelectedMenu(HOME_MENU_ROUTE_OPTION);
                    } else {
                        setLinkType(null);
                    }
                } else if (features.links.website) {
                    setLinkType('Website');
                    setLinkUrl(existingUrl);
                } else if (
                    existingLinkType === 'asset' &&
                    features.links.asset
                ) {
                    setLinkType('Asset');
                    setSelectedAssetLink({
                        name: existingText,
                        url: existingUrl,
                    });
                } else {
                    setLinkType(null);
                }
            } else {
                setLinkType(null);
            }

            setLinkDisplayText(existingText || '');
        }

        setPopupContent(buildLinkPopupContent(linkType));
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    useEffect(() => {
        if (selectedSubItem === 'link') {
            setPopupContent(buildLinkPopupContent(linkType));
        }
    }, [
        linkType,
        selectedPortal,
        ssoId,
        selectedMenu,
        selectedAssetLink,
        linkDisplayText,
        linkUrl,
    ]);

    useEffect(() => {
        const isViewingAssetLinkType =
            isPopupOpen && selectedSubItem === 'link' && linkType === 'Asset';

        if (!isViewingAssetLinkType) {
            assetLibraryAutoOpenedRef.current = false;
            return;
        }

        if (!selectedAssetLink && !assetLibraryAutoOpenedRef.current) {
            assetLibraryAutoOpenedRef.current = true;
            setIsAddFromAssetsChecked('assetLink');
        }
    }, [isPopupOpen, selectedSubItem, linkType, selectedAssetLink]);

    const handleTableClick = event => {
        setSelectedSubItem('tableAdd');

        let rows = '';
        let cols = '';
        let content = null;

        content = (
            <div>
                <p className="modal-header-text">
                    {getText('msg_964fee05e331')}
                </p>
                <Divider sx={{ backgroundColor: 'gray' }} />
                <div className="modal-input-row">
                    <div className="file-form-label modal-input-width-80">
                        {getText('msg_5d08f9511d95')}
                    </div>
                    <input
                        onChange={event => (rows = event.target.value)}
                        defaultValue=""
                        className="form-control modal-text-input modal-text-input-narrow modal-text-input-inline-margin"
                        type="text"
                        aria-label={getText('msg_ae5afe234b14')}
                    ></input>
                </div>
                <div className="modal-input-row">
                    <div className="file-form-label modal-input-width-80">
                        {getText('msg_6d97d8442f76')}
                    </div>
                    <input
                        onChange={event => (cols = event.target.value)}
                        defaultValue=""
                        className="form-control modal-text-input modal-text-input-narrow modal-text-input-inline-margin"
                        type="text"
                        aria-label={getText('msg_ae5afe234b14')}
                    ></input>
                </div>
                <div
                    onClick={() => handleInsertTable(rows, cols)}
                    type="button"
                    className="float-end button-base ing-button-primary-left modal-submit-button"
                >
                    {getText('msg_fc45f9b7a9a6')}
                </div>
            </div>
        );

        setPopupContent(content);
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    const handleTableEditClick = event => {
        setSelectedSubItem('tableEdit');

        let content = null;

        content = (
            <div>
                <p className="modal-header-text">
                    {getText('msg_4d6c9669ea8a')}
                </p>
                <Divider sx={{ backgroundColor: 'gray' }} />
                <p
                    className="modal-menu-item"
                    onClick={() => {
                        editor.chain().focus().addColumnBefore().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_280fa57559aa')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().addColumnAfter().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_66d50530bd85')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().addRowBefore().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_0f73c7472331')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().addRowAfter().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_4d976766c5e6')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().deleteRow().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_bd9ff1e94e2b')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().deleteColumn().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_e8bae6c1846e')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().deleteTable().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_c233fd166873')}
                </p>
            </div>
        );

        setPopupContent(content);
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    const handleTableCellClick = event => {
        setSelectedSubItem('tableCell');

        let content = null;

        content = (
            <div>
                <p className="modal-header-text">
                    {getText('msg_783d66a925a8')}
                </p>
                <Divider sx={{ backgroundColor: 'gray' }} />
                <p
                    className="modal-menu-item"
                    onClick={() => {
                        editor.chain().focus().mergeCells().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_8434e3f00b97')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={() => {
                        editor.chain().focus().splitCell().run();
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                >
                    {getText('msg_197761e4b448')}
                </p>
                <p
                    className="modal-menu-item-continued"
                    onClick={event => {
                        setAnchorCC(event.currentTarget);
                        setIsCellColorPopoverOpen(true);
                    }}
                >
                    {getText('msg_1d0c8304baed')}
                </p>
            </div>
        );

        setPopupContent(content);
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    const getSelectedText = () => {
        const slice = editor.view.state.selection.content();
        const serializer = DOMSerializer.fromSchema(editor.schema);
        const fragment = serializer.serializeFragment(slice.content);
        const div = document.createElement('div');
        div.appendChild(fragment);
        return div.textContent;
    };

    const formatNotificationTokenLabel = tokenName => {
        return tokenName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/(\D)(\d+)/g, '$1 $2')
            .trim();
    };

    const mapNotificationTokenOption = token => {
        if (!token?.name) {
            return null;
        }

        return {
            label: formatNotificationTokenLabel(token.name),
            value: `[${token.name}]`,
            attributes: token.attributes || [],
        };
    };

    useEffect(() => {
        let isSubscribed = true;

        const loadNotificationTokenOptions = async () => {
            try {
                const notificationTokens = await fetchNotificationTokens();
                const tokens = Array.isArray(notificationTokens)
                    ? notificationTokens
                    : [];
                const dataPointTokens = tokens.filter(token =>
                    isDataPointReturnTokenName(token?.name),
                );
                const options = tokens
                    .filter(token => !isDataPointReturnTokenName(token?.name))
                    .map(mapNotificationTokenOption)
                    .filter(Boolean);

                // The data point returns are separate tokens on the backend but they are
                // offered as a single token whose Return dropdown picks the tag name.
                if (dataPointTokens.length > 0) {
                    options.push({
                        label: 'Data Point',
                        value: `[${DATA_POINT_TOKEN_NAME}]`,
                        attributes: dataPointTokens[0].attributes || [],
                        returnOptions: dataPointTokens.map(token => ({
                            label: formatNotificationTokenLabel(token.name)
                                .replace('Data Point', '')
                                .trim(),
                            value: token.name,
                        })),
                    });
                }

                if (isSubscribed) {
                    setNotificationTokenOptions(
                        options.sort((first, second) =>
                            first.label.localeCompare(second.label),
                        ),
                    );
                }
            } catch (error) {
                console.error('Could not fetch notification tokens:', error);
                Toast.error(getText('msg_3f960d283e7b'));
            }
        };

        loadNotificationTokenOptions();

        return () => {
            isSubscribed = false;
        };
    }, []);

    const selectedTokenOption = notificationTokenOptions.find(
        option => option.value === selectedTokenValue,
    );
    const selectedTokenName = getTokenName(selectedTokenValue);
    const baseTokenName =
        selectedTokenName === DATA_POINT_TOKEN_NAME
            ? dataPointReturn
            : selectedTokenName;
    const tokenAttributes = selectedTokenOption?.attributes || [];
    const disabledTokenAttributeNames =
        getDisabledTokenAttributeNames(baseTokenName);
    const enabledTokenAttributes = tokenAttributes.filter(
        attribute => !disabledTokenAttributeNames.includes(attribute.name),
    );

    useEffect(() => {
        const usesPlanType = (selectedTokenOption?.attributes || []).some(
            attribute => attribute.name === 'PlanType',
        );

        if (
            !isTokenParameterStep ||
            !usesPlanType ||
            planTypeOptions.length > 0
        ) {
            return;
        }

        queryPlanTypes()
            .then(planTypes =>
                setPlanTypeOptions(
                    (planTypes || [])
                        .filter(planType => planType?.code)
                        .map(planType => ({
                            label: planType.name || planType.code,
                            value: planType.code,
                        })),
                ),
            )
            .catch(() => Toast.error('Could not fetch plan types'));
    }, [isTokenParameterStep, selectedTokenValue]);

    useEffect(() => {
        const usesCategory = (selectedTokenOption?.attributes || []).some(
            attribute => attribute.name === 'Category',
        );

        if (
            !isTokenParameterStep ||
            !usesCategory ||
            dataPointCategories.length > 0
        ) {
            return;
        }

        getDataPointCategories()
            .then(categories => setDataPointCategories(categories || []))
            .catch(() => Toast.error('Could not fetch data point categories'));
    }, [isTokenParameterStep, selectedTokenValue]);

    useEffect(() => {
        const category = dataPointCategories.find(
            dataPointCategory =>
                dataPointCategory.code === tokenAttributeValues.Category,
        );

        if (!category) {
            setDataPointSubCategories([]);
            return;
        }

        getDataPointSubCategories([category.id])
            .then(subCategories =>
                setDataPointSubCategories(subCategories || []),
            )
            .catch(() =>
                Toast.error('Could not fetch data point subcategories'),
            );
    }, [tokenAttributeValues.Category, dataPointCategories]);

    const updateTokenAttributeValue = (name, value) => {
        setTokenAttributeValues(previousValues => ({
            ...previousValues,
            [name]: value,
        }));
    };

    const resetTokenSelection = () => {
        setToken('');
        setSelectedTokenValue('');
        setIsTokenParameterStep(false);
        setTokenAttributeValues({});
        setDataPointReturn('');
        setDataPointSubCategories([]);
    };

    const handleTokenSelectionChange = tokenValue => {
        setToken('');
        setSelectedTokenValue(tokenValue);
        setIsTokenParameterStep(false);
        setTokenAttributeValues({});
        setDataPointReturn('');
    };

    const insertTokenTag = tag => {
        editor.chain().focus().insertContent(tag).run();
        resetTokenSelection();
        setAnchorEl(null);
        setIsPopupOpen(false);
        setSelectedSubItem('');
    };

    const validateTokenAttributes = () => {
        const missingAttribute = enabledTokenAttributes.find(
            attribute =>
                attribute.isRequired && !tokenAttributeValues[attribute.name],
        );

        if (missingAttribute) {
            Toast.error(
                `${formatNotificationTokenLabel(missingAttribute.name)} is required`,
            );
            return false;
        }

        const { DateFrom, DateTo } = tokenAttributeValues;

        if (DateFrom && DateTo && DateFrom > DateTo) {
            Toast.error('Date From cannot be after Date To');
            return false;
        }

        return true;
    };

    const insertParameterizedToken = () => {
        if (!baseTokenName) {
            Toast.error('Return is required');
            return;
        }

        if (!validateTokenAttributes()) {
            return;
        }

        const parameters = enabledTokenAttributes.reduce(
            (accumulatedParameters, attribute) => ({
                ...accumulatedParameters,
                [attribute.name]: tokenAttributeValues[attribute.name],
            }),
            {},
        );

        insertTokenTag(buildTokenTag(baseTokenName, parameters));
    };

    const handleTokenPrimaryAction = () => {
        if (!selectedTokenValue) {
            Toast.error('Select a token');
            return;
        }

        const usesAttributeForm = tokenUsesAttributeForm(selectedTokenOption);

        if (usesAttributeForm && !isTokenParameterStep) {
            setIsTokenParameterStep(true);
            return;
        }

        if (usesAttributeForm) {
            insertParameterizedToken();
            return;
        }

        setToken(selectedTokenValue);
    };

    const renderTokenAttributeInput = (attribute, isDisabled) => {
        if (attribute.name === 'PlanType') {
            return (
                <Dropdown
                    allowSearch={true}
                    disabled={isDisabled}
                    options={planTypeOptions}
                    defaultValue={
                        planTypeOptions.find(
                            option =>
                                option.value === tokenAttributeValues.PlanType,
                        )?.label || 'Select Plan Type'
                    }
                    onChange={value =>
                        updateTokenAttributeValue('PlanType', value)
                    }
                    maxOpenHeight={'200px'}
                    overlappingOptions={false}
                />
            );
        }

        if (attribute.name === 'Category') {
            const categoryOptions = dataPointCategories.map(category => ({
                label: category.name || category.code,
                value: category.code,
            }));

            return (
                <Dropdown
                    allowSearch={true}
                    disabled={isDisabled}
                    options={categoryOptions}
                    defaultValue={
                        categoryOptions.find(
                            option =>
                                option.value === tokenAttributeValues.Category,
                        )?.label || 'Select Category'
                    }
                    onChange={value => {
                        updateTokenAttributeValue('Category', value);
                        updateTokenAttributeValue('SubCategory', '');
                    }}
                    maxOpenHeight={'200px'}
                    overlappingOptions={false}
                />
            );
        }

        if (attribute.name === 'SubCategory') {
            const subCategoryOptions = dataPointSubCategories.map(
                subCategory => ({
                    label: subCategory.name || subCategory.code,
                    value: subCategory.code,
                }),
            );

            return (
                <Dropdown
                    allowSearch={true}
                    disabled={isDisabled || !tokenAttributeValues.Category}
                    options={subCategoryOptions}
                    defaultValue={
                        subCategoryOptions.find(
                            option =>
                                option.value ===
                                tokenAttributeValues.SubCategory,
                        )?.label || 'All Sub Categories'
                    }
                    onChange={value =>
                        updateTokenAttributeValue('SubCategory', value)
                    }
                    maxOpenHeight={'200px'}
                    overlappingOptions={false}
                />
            );
        }

        if (DATE_TOKEN_ATTRIBUTE_NAMES.includes(attribute.name)) {
            return (
                <CustomDatePicker
                    selectedDate={tokenAttributeValues[attribute.name] || null}
                    onDateChange={value =>
                        updateTokenAttributeValue(attribute.name, value)
                    }
                    disabled={isDisabled}
                    minDate={
                        attribute.name === 'DateTo'
                            ? parseTokenParameterDate(
                                  tokenAttributeValues.DateFrom,
                              )
                            : null
                    }
                    maxDate={
                        attribute.name === 'DateFrom'
                            ? parseTokenParameterDate(
                                  tokenAttributeValues.DateTo,
                              )
                            : null
                    }
                    hideErrorMessage={true}
                />
            );
        }

        return (
            <input
                className="form-control rte-link-input"
                type="text"
                disabled={isDisabled}
                value={tokenAttributeValues[attribute.name] || ''}
                onChange={event =>
                    updateTokenAttributeValue(
                        attribute.name,
                        event.target.value,
                    )
                }
                aria-label={`Enter ${formatNotificationTokenLabel(attribute.name)}`}
            />
        );
    };

    const renderTokenAttributeField = attribute => {
        const isDisabled = disabledTokenAttributeNames.includes(attribute.name);

        return (
            <div className="rte-link-form-group" key={attribute.name}>
                <div
                    className={`file-form-label${isDisabled ? ' rte-token-disabled-label' : ''}`}
                >
                    {formatNotificationTokenLabel(attribute.name)}
                    {attribute.isRequired && !isDisabled && (
                        <span className="rte-required-asterisk">*</span>
                    )}
                </div>
                {renderTokenAttributeInput(attribute, isDisabled)}
            </div>
        );
    };

    const renderDataPointReturnField = returnOptions => (
        <div className="rte-link-form-group">
            <div className="file-form-label">
                Return
                <span className="rte-required-asterisk">*</span>
            </div>
            <Dropdown
                options={returnOptions}
                defaultValue={
                    returnOptions.find(
                        option => option.value === dataPointReturn,
                    )?.label || 'Select Return'
                }
                onChange={setDataPointReturn}
                maxOpenHeight={'200px'}
                overlappingOptions={false}
            />
        </div>
    );

    const buildTokenPopupContent = (tokenValue, showParameters) => {
        const tokenOption = notificationTokenOptions.find(
            option => option.value === tokenValue,
        );
        const usesAttributeForm = tokenUsesAttributeForm(tokenOption);

        return (
            <div className="rte-token-popup">
                <div className="rte-token-popup-header">
                    {showParameters && (
                        <button
                            type="button"
                            className="rte-token-back-button"
                            aria-label="Back to token selection"
                            onClick={() => setIsTokenParameterStep(false)}
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                    )}
                    <p className="rte-token-popup-title">
                        {showParameters ? tokenOption?.label : 'Insert Token'}
                    </p>
                </div>
                <Divider className="rte-link-popup-divider" />

                {!showParameters && (
                    <div className="rte-link-form-group">
                        <Dropdown
                            allowSearch={true}
                            options={notificationTokenOptions}
                            defaultValue={tokenOption?.label || 'Select Token'}
                            onChange={handleTokenSelectionChange}
                            maxOpenHeight={'200px'}
                            overlappingOptions={false}
                        />
                    </div>
                )}

                {showParameters &&
                    tokenOption?.returnOptions &&
                    renderDataPointReturnField(tokenOption.returnOptions)}

                {showParameters &&
                    tokenAttributes.map(renderTokenAttributeField)}

                <div className="rte-link-popup-footer">
                    <button
                        type="button"
                        className="button-base ing-button-primary-left rte-link-insert-button rte-token-insert-button"
                        onClick={handleTokenPrimaryAction}
                    >
                        {usesAttributeForm && !showParameters
                            ? 'Continue'
                            : 'Insert'}
                    </button>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (selectedSubItem === 'token' && !token) {
            setPopupContent(
                buildTokenPopupContent(
                    selectedTokenValue,
                    isTokenParameterStep,
                ),
            );
        }
    }, [
        selectedSubItem,
        token,
        selectedTokenValue,
        isTokenParameterStep,
        tokenAttributeValues,
        dataPointReturn,
        notificationTokenOptions,
        planTypeOptions,
        dataPointCategories,
        dataPointSubCategories,
    ]);

    const handleInsertTable = (rows, cols) => {
        editor
            .chain()
            .focus()
            .insertTable({ cols: cols, rows: rows, withHeaderRow: true })
            .run();
        setAnchorEl(null);
        setIsPopupOpen(false);
        setSelectedSubItem('');
    };

    useEffect(() => {
        let content = '';
        switch (token) {
            case '[IndivRegQR]':
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_66674cc698f4')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <p>{getText('msg_5a2921cab3f8')}</p>
                        </div>
                        <div
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .insertContent('[IndivRegQR]')
                                    .run();
                                setAnchorEl(null);
                                setIsPopupOpen(false);
                                setSelectedSubItem('');
                            }}
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_fc45f9b7a9a6')}
                        </div>
                    </div>
                );
                setPopupContent(content);
                break;
            case '[IndivRegLink]':
                let str = '';
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_07f5d328c4d1')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <p>{getText('msg_12ce1388a2d9')}</p>
                            <div className="file-form-label modal-input-margin-top">
                                {getText('msg_35209321d117')}
                                <span className="modal-required-asterisk">
                                    *
                                </span>
                            </div>
                            <input
                                onChange={event => (str = event.target.value)}
                                defaultValue={str}
                                className="form-control modal-text-input"
                                type="text"
                                placeholder={getText('msg_afbc6def06b9')}
                                aria-label={getText('msg_f640c574cde2')}
                            ></input>
                        </div>
                        <div
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .insertContent(
                                        '[IndivRegLink DisplayText="' +
                                            str +
                                            '"]',
                                    )
                                    .run();
                                setAnchorEl(null);
                                setIsPopupOpen(false);
                                setSelectedSubItem('');
                            }}
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_95802daab3a2')}
                        </div>
                    </div>
                );
                setPopupContent(content);
                break;
            default:
                if (token !== '') {
                    editor.chain().focus().insertContent(token).run();
                    setAnchorEl(null);
                    setIsPopupOpen(false);
                    setSelectedSubItem('');
                }
                break;
        }
    }, [token]);

    const handleButtonClick = (event, item) => {
        let content = null;

        setSelectedSubItem(item);

        switch (item) {
            case 'sso':
                let displayTextSSO = '';
                let selectedSSO = 0;
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_d452b54f2d73')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <div className="file-form-label">
                                {getText('msg_35209321d117')}
                                <span className="modal-required-asterisk">
                                    *
                                </span>
                            </div>
                            <input
                                onChange={event =>
                                    (displayTextSSO = event.target.value)
                                }
                                defaultValue={displayTextSSO}
                                className="form-control modal-text-input"
                                type="text"
                                placeholder={getText('msg_afbc6def06b9')}
                                aria-label={getText('msg_fe82383e1c81')}
                            ></input>
                        </div>
                        <div className="modal-input-container">
                            <div className="file-form-label">
                                {getText('msg_b859366b8f24')}
                                <span className="modal-required-asterisk">
                                    *
                                </span>
                            </div>
                            <SSODropdown
                                inlineLabel={false}
                                label=""
                                selectedSSO={selectedSSO}
                                onSSOChange={ssoId => (selectedSSO = ssoId)}
                                client={true}
                            />
                        </div>
                        <div
                            onClick={() =>
                                setLink(
                                    `https://ssoId:${selectedSSO}`,
                                    displayTextSSO,
                                )
                            }
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_95802daab3a2')}
                        </div>
                    </div>
                );
                break;
            case 'link':
                break;
            case 'phone':
                break;
            case 'image':
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_a2d43587989e')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <p
                            className="modal-menu-item"
                            onClick={() => {
                                handleAddFromAssets('image');
                            }}
                        >
                            {getText('msg_27eb0a10db5c')}
                        </p>
                        <p
                            className="modal-menu-item"
                            onClick={() => {
                                handleUploadFromDevice('image');
                            }}
                        >
                            {getText('msg_c002fe6df29c')}
                        </p>
                    </div>
                );
                break;
            case 'video':
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_805f36a93d91')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <p
                            className="modal-menu-item"
                            onClick={() => {
                                handleAddFromAssets('video');
                            }}
                        >
                            {getText('msg_27eb0a10db5c')}
                        </p>
                        <p
                            className="modal-menu-item"
                            onClick={() => {
                                handleUploadFromDevice('video');
                            }}
                        >
                            {getText('msg_c002fe6df29c')}
                        </p>
                    </div>
                );
                break;
            case 'qr':
                var qrInput = '';
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_66674cc698f4')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <div className="file-form-label">
                                {getText('msg_1cd49c79f9cf')}
                                <span className="modal-required-asterisk">
                                    *
                                </span>
                            </div>
                            <input
                                defaultValue={qrInput}
                                onChange={event =>
                                    (qrInput = event.target.value)
                                }
                                className="form-control modal-text-input"
                                type="text"
                                placeholder={getText('msg_ae5afe234b14')}
                                aria-label={getText('msg_ae5afe234b14')}
                            />
                        </div>
                        <div
                            onClick={() => {
                                const ok = editor?.commands.insertQRCode({
                                    url: qrInput || 'https://example.com',
                                });
                                if (!ok) console.warn('Insert QR failed');
                            }}
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_fc45f9b7a9a6')}
                        </div>
                    </div>
                );
                setPopupContent(content);
                setAnchorEl(event.currentTarget);
                setIsPopupOpen(true);
                break;
            case 'token':
                let tokenLabel = '';
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_393a141b4a0c')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <Dropdown
                                defaultValue={'Select Token'}
                                allowSearch={true}
                                onChange={option => {
                                    setToken('');
                                    tokenLabel = option;
                                }}
                                options={notificationTokenOptions}
                            />
                        </div>
                        <div
                            onClick={() => {
                                setToken(tokenLabel);
                            }}
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_95802daab3a2')}
                        </div>
                    </div>
                );
                break;
            case 'token2':
                content = (
                    <div>
                        <p className="modal-header-text">
                            {getText('msg_66674cc698f4')}
                        </p>
                        <Divider sx={{ backgroundColor: 'gray' }} />
                        <div className="modal-input-container">
                            <p>{getText('msg_5a2921cab3f8')}</p>
                        </div>
                        <div
                            onClick={() => null}
                            type="button"
                            className="float-end button-base ing-button-primary-left modal-submit-button"
                        >
                            {getText('msg_fc45f9b7a9a6')}
                        </div>
                    </div>
                );
                break;
            case 'table':
                break;
            default:
                content = null;
        }

        setPopupContent(content);
        setAnchorEl(event.currentTarget);
        setIsPopupOpen(true);
    };

    const handleClose = () => {
        resetTokenSelection();
        setSelectedSubItem('');
        setAnchorEl(null);
        setIsPopupOpen(false);
    };

    function normalizeHtmlForEditorLoad(html) {
        if (typeof html !== 'string' || !html) {
            return html || '';
        }

        const cleanedHtml = html.replace(/ portalid="[^"]*"/g, '');

        return cleanedHtml.replace(
            /<div>\s*<br\s*\/?>\s*<\/div>/gi,
            '<div></div>',
        );
    }

    function normalizeHtmlForEditorSave(html) {
        if (typeof html !== 'string' || !html) {
            return html || '';
        }

        const normalizedHtml = html
            .replace(/<\/div>\s*<br\s*\/?>\s*(?=<div)/gi, '</div>')
            .replace(/<div>\s*<\/div>/gi, '<div><br></div>')
            .replace(
                /<div>\s*<br\s*\/?>\s*\/?>\s*<\/div>/gi,
                '<div><br></div>',
            );

        return useAbsoluteLinks
            ? toAbsoluteAppLinks(normalizedHtml)
            : normalizedHtml;
    }

    function convertIndivRegLinkPlaceholderToToken(html) {
        if (typeof html !== 'string' || !html) {
            return html || '';
        }

        return html.replace(
            /<span[^>]*title=["']IndivRegLinkPlaceholder["'][^>]*>([\s\S]*?)<\/span>\s*/gi,
            (_, displayMarkup) => {
                const displayText = displayMarkup
                    .replace(/<[^>]+>/g, '')
                    .replace(/"/g, '&quot;')
                    .trim();

                return `[IndivRegLink DisplayText="${displayText}"]`;
            },
        );
    }

    const CustomTableCell = TableCell.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                backgroundColor: {
                    default: null,
                    parseHTML: element =>
                        element.getAttribute('data-background-color'),
                    renderHTML: attributes => {
                        return {
                            'data-background-color': attributes.backgroundColor,
                            style: `background-color: ${attributes.backgroundColor}`,
                        };
                    },
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            PortalLinkNode.configure({
                interactive: false,
                onBlockedClick: null,
            }),
            StarterKit.configure({
                paragraph: false,
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` because marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false, // TODO : Making this as `false` because marks are not preserved when I try to preserve attrs, awaiting a bit of help
                },
            }),
            Placeholder.configure({
                placeholder: placeholderText || getText('msg_8b4539a50a3b'),
            }),
            Underline,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto', 'http', 'https'],
                validate: href => !href.includes('portal-page/'),
                target: '_blank',
                HTMLAttributes: {
                    class: '',
                },
                openOnClick: false,
            }),
            ResizableImage.configure({
                resize: EDITOR_IMAGE_RESIZE_OPTIONS,
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: '',
        editorProps: {
            transformPastedHTML: html => sanitizePastedHtml(html, features),
        },
        onUpdate: ({ editor }) => {
            if (typeof onContentUpdate !== 'function') {
                return;
            }

            if (isSettingInitialContentRef.current) {
                return;
            }

            if (!debouncedContentUpdateRef.current) {
                return;
            }

            if (!hasInitializedRef.current) {
                return;
            }

            const rawHTML = editor.getHTML();
            const currentAssetIds = textAssets.map(asset => asset.assetId);
            const embeddedAssetIds = Array.from(editor.getJSON().content || [])
                .map(node => node.attrs?.assetId)
                .filter(Boolean);
            const deletedAssets = currentAssetIds.filter(
                assetId => !embeddedAssetIds.includes(assetId),
            );

            deletedAssets.forEach(assetId => {
                if (onAssetDelete) {
                    onAssetDelete(assetId);
                }
            });

            const normalizedHTML = normalizeHtmlForEditorSave(rawHTML);
            const saveContent =
                convertIndivRegLinkPlaceholderToToken(normalizedHTML);

            if (normalizedHTML.includes('[IndivRegQR]')) {
                replaceTokensWithPlaceholder(editor.getHTML());
                return;
            }

            debouncedContentUpdateRef.current(saveContent);
        },
    });

    useEffect(() => {
        if (!editor || (!isPreview && !isViewLive)) return;
        const dom = editor.view.dom;
        function handleLinkClick(e) {
            const anchor = e.target.closest('a');
            if (!anchor) return;
            const href = anchor.getAttribute('href');
            if (isAssetLinkAnchor(anchor)) {
                e.preventDefault();
                e.stopPropagation();
                showModal({
                    type: ModalTypes.ASSET_VIEW,
                    title: anchor.textContent?.trim() || 'Asset Preview',
                    text: href,
                });
                return;
            }

            if (isMenuLink(href)) {
                e.preventDefault();
                e.stopPropagation();
                Toast.info(getText('msg_7ebb77bbfd9c'));
            } else if (isSSOLink(href)) {
                e.preventDefault();
                e.stopPropagation();
                Toast.info(
                    getText('msg_29393dec2c1f'),
                    {},
                    getText('msg_6c8145e3a0f9'),
                );
            }
        }
        dom.addEventListener('click', handleLinkClick, true);
        return () => dom.removeEventListener('click', handleLinkClick, true);
    }, [editor, isPreview, isViewLive, showModal]);

    useEffect(() => {
        if (typeof onContentUpdate !== 'function') {
            return;
        }

        debouncedContentUpdateRef.current = debounce(html => {
            onContentUpdate(html);
        }, 250);

        return () => {
            if (debouncedContentUpdateRef.current) {
                debouncedContentUpdateRef.current.cancel();
            }
        };
    }, [onContentUpdate]);

    useEffect(() => {
        if (!editor) {
            return;
        }

        if (initialContent === null) {
            return;
        }

        if (hasInitializedRef.current) {
            return;
        }

        isSettingInitialContentRef.current = true;

        const normalizedContent = normalizeHtmlForEditorLoad(initialContent);
        editor.commands.setContent(normalizedContent, { emitUpdate: false });
        replaceTokensWithPlaceholder(normalizedContent);

        setTimeout(() => {
            isSettingInitialContentRef.current = false;
            hasInitializedRef.current = true;
        }, 150);
    }, [editor, initialContent]);

    const replaceTokensWithPlaceholder = initialContent => {
        const currentContent = initialContent;
        let updatedContent = currentContent;

        // Check if "[IndRegQR]" exists in the content
        const index = currentContent.indexOf('[IndivRegQR]');
        if (index !== -1) {
            // Remove "[IndRegQR]" from the content
            updatedContent = currentContent.replace(/\[IndivRegQR\]/g, '');

            // Move the cursor to the position where "[IndRegQR]" was
            editor.commands.setTextSelection(index); // Set the selection to the position of "[IndRegQR]"

            editor.commands.setContent(
                normalizeHtmlForEditorLoad(updatedContent),
                { emitUpdate: false },
            );

            // Insert the image at the position where "[IndRegQR]" was
            //editor.chain().focus().setImage({ src: imageUrl }).run();
            editor
                .chain()
                .focus()
                .insertContent(
                    `<img src="https://ingaged-dev-ingress.ext.dv.aws-uset1.oss2.mrshmc.com/v1/files/asset/150/PlaceholderQR_20241118_214050615985.png" style="width: fit-content; height: auto; cursor: pointer; margin: 0px auto;" draggable="true"></img>`,
                )
                .run();
        }
    };

    const replaceIndRegQRWithImage = (initialContent, imageUrl) => {
        // Get the current content of the editor
        const currentContent = initialContent;
        let updatedContent = currentContent;

        // Check if "[IndRegQR]" exists in the content
        const index = currentContent.indexOf('[IndivRegQR]');
        if (index !== -1) {
            // Remove "[IndRegQR]" from the content
            updatedContent = currentContent.replace(/\[IndivRegQR\]/g, '');

            editor.commands.setContent(updatedContent, { emitUpdate: false });

            // Move the cursor to the position where "[IndRegQR]" was
            editor.commands.setTextSelection(index); // Set the selection to the position of "[IndRegQR]"

            // Insert the image at the position where "[IndRegQR]" was
            //editor.chain().focus().setImage({ src: imageUrl }).run();
            editor
                .chain()
                .focus()
                .insertContent(
                    `<img src="https://ingaged-dev-ingress.ext.dv.aws-uset1.oss2.mrshmc.com/v1/files/asset/150/PlaceholderQR_20241118_214050615985.png" style="width: fit-content; height: auto; cursor: pointer; margin: 0px auto;" draggable="true"></img>`,
                )
                .run();
        }
    };

    const replaceIndRegLinkWithHyperlink = initialContent => {
        let updatedContent = initialContent;

        // Regular expression to match the [IndRegLink DisplayText="..."] pattern
        const regex = /\[IndivRegLink DisplayText="([^"]+)"\]/g;
        let match;

        // Loop through all matches in the content
        while ((match = regex.exec(initialContent)) !== null) {
            const index = initialContent.indexOf('[IndivRegLink');
            const displayText = match[1]; // Extract the display text

            updatedContent = initialContent.replace(
                /\[IndivRegLink DisplayText="([^"]+)"\]/g,
                '',
            );

            editor.commands.setContent(updatedContent, { emitUpdate: false });

            // Move the cursor to the position where "[IndRegQR]" was
            editor.commands.setTextSelection(index); // Set the selection to the position of "[IndRegQR]"

            editor
                .chain()
                .focus()
                .insertContent(
                    `<span title="IndivRegLinkPlaceholder" style="color: blue; cursor: pointer"><u>${displayText}</u></span>`,
                )
                .run();
        }
    };

    useEffect(() => {
        // Check if any asset was deleted
        const assetDeleted = prevContainerAssets.some(
            prevAsset =>
                !textAssets.some(
                    currentAsset => currentAsset.assetId === prevAsset.assetId,
                ),
        );

        // If an asset was deleted, do not set a new asset
        if (assetDeleted) {
            setPrevContainerAssets(textAssets); // Update previous state
            return; // Exit early
        }

        // Find the most recently added asset
        const newAsset = textAssets.find((asset, index) => {
            // Check if the asset is new (not in the previous state)
            return (
                !prevContainerAssets[index] ||
                asset.assetId !== prevContainerAssets[index]?.assetId
            );
        });

        // If a new asset is found, use its URL
        if (newAsset) {
            if (newAsset.assetFileType === 'image') {
                const imageUrl = newAsset.url; // Get the URL of the new asset
                const imageWidth = newAsset.width;
                const imageMargin = newAsset.margin;
                insertImage(imageUrl, imageWidth, imageMargin);
            } else if (newAsset.assetFileType === 'video') {
                insertVideo(newAsset.url);
            }
            setPrevContainerAssets(textAssets); // Update previous state
        }
    }, [textAssets, editor]);

    useEffect(() => {
        if (editor && initialContent !== null) {
            if (initialContent && initialContent.includes('[IndivRegQR]')) {
                replaceTokensWithPlaceholder(initialContent);
            } else {
                const processedContent =
                    normalizeHtmlForEditorLoad(initialContent);
                editor.commands.setContent(processedContent, {
                    emitUpdate: false,
                    parseOptions: { preserveWhitespace: 'full' },
                });
            }
        }
    }, [editor, initialContent]);

    useEffect(() => {
        if (editor) {
            const updateTableState = () => {
                const isInsideTable = editor.isActive('table');
                setIsTableSelected(isInsideTable);
            };

            editor.on('update', updateTableState);
            editor.on('selectionUpdate', updateTableState);

            return () => {
                editor.off('update', updateTableState);
                editor.off('selectionUpdate', updateTableState);
            };
        }
    }, [editor]);

    if (!editor) {
        return null;
    }
    const insertTable = () => {
        const col = window.prompt(getText('msg_caa849d2a632'));

        if (col) {
            editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: col, withHeaderRow: true })
                .run();
        }
    };

    const setFontStyle = fontFamily => {
        if (editor) {
            editor.chain().focus().setFontFamily(fontFamily).run();
        }
    };

    const setFontSize = fontSize => {
        if (editor) {
            editor.chain().focus().setFontSize(fontSize).run();
        }
    };

    const getHTMLFromSelection = () => {
        const slice = editor.view.state.selection.content();
        const serializer = DOMSerializer.fromSchema(editor.schema);
        const fragment = serializer.serializeFragment(slice.content);
        const div = document.createElement('div');
        div.appendChild(fragment);
        return div.innerHTML;
    };

    const handleCopy = () => {
        const html = getHTMLFromSelection();

        const clipboardItem = new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([html], { type: 'text/plain' }),
        });

        navigator.clipboard.write([clipboardItem]).then(
            _ => console.log('clipboard.write() Ok'),
            error => alert(error),
        );
    };

    async function getHTMLFromClipboardContents(clipboardContents) {
        for (const item of clipboardContents) {
            if (item.types.includes('text/html')) {
                const blob = await item.getType('text/html');
                const blobText = await blob.text();
                return blobText;
            }
        }

        return null;
    }

    const handlePaste = async () => {
        try {
            const clipboardContents = await navigator.clipboard.read();
            const html = await getHTMLFromClipboardContents(clipboardContents);
            const sanitizedHtml = sanitizePastedHtml(html, features);
            editor.chain().focus().insertContent(sanitizedHtml).run();
        } catch (error) {
            console.error('error');
        }
    };

    const handleCut = () => {
        handleCopy();

        const { state, dispatch } = editor.view;
        const { from, to } = state.selection;
        if (from !== to) {
            dispatch(state.tr.delete(from, to));
        }
    };

    const handleMenuItemClick = (event, item) => {
        event.stopPropagation();

        setSelectedItem(item);
        if (item === 'emoji') {
            setIsEmojiPickerOpen(!isEmojiPickerOpen);
        } else {
            setIsEmojiPickerOpen(false);
        }
    };

    const insertImage = (
        imageUrl,
        imageWidth = null,
        imageMargin = '0px auto 0px 0px',
    ) => {
        editor
            .chain()
            .focus()
            .setImage({
                src: imageUrl,
                width: parsePixelWidth(imageWidth),
                margin: parseImageMarginValue(imageMargin),
            })
            .run();
    };

    const insertVideo = videoUrl => {
        if (!editor) return;
        editor.commands.insertVideo({
            src: videoUrl,
            controls: true,
        });
    };

    useEffect(() => {
        setFormData(prevDetails => ({
            ...prevDetails,
            plainTextBody: plainText,
        }));
    }, [plainText]);

    useEffect(() => {
        const updatedTextAssets = textAssets.filter(asset =>
            text.includes(asset.url),
        );
        setTextAssets(updatedTextAssets);
    }, [text]);

    useEffect(() => {
        if (isAddFromAssetsChecked.length) {
            loadAssets(isAddFromAssetsChecked);
        }
    }, [isAddFromAssetsChecked]);

    const handleFileChange = fileValue => {
        setFile(sanitizeFileName(fileValue));
    };

    const resolveUploadAgencyId = () => {
        if (agencyId !== undefined && agencyId !== null && agencyId !== '') {
            return Number(agencyId);
        }

        const userAgencies = Array.isArray(user?.jwt?.agencies)
            ? user.jwt.agencies
            : [];

        if (userAgencies.length > 0) {
            return Number(userAgencies[0]);
        }

        return 0;
    };

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            newAsset.agencyId = resolveUploadAgencyId();
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 1;
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const fileCategory = getFileCategory(file.name);
            if (fileCategory === 'video') {
                newAsset.metaData = await getVideoRuntime(file);
            } else if (fileCategory === 'audio') {
                newAsset.metaData = await getAudioLength(file);
            } else if (fileCategory === 'image') {
                newAsset.metaData = await getImageDimensions(file);
            }

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                switch (isUploadFromDeviceChecked) {
                    case 'image':
                    case 'video':
                        setTextAssets(prevTextAssets => {
                            // Only add the asset if it doesn't already exist

                            const assetExists = prevTextAssets.some(
                                asset =>
                                    asset.assetId === createResponse.data.id,
                            );
                            if (!assetExists) {
                                return [
                                    ...prevTextAssets,
                                    {
                                        assetId: createResponse.data.id,
                                        assetTypeId:
                                            createResponse.data.assetTypeId,
                                        url: createResponse.data.url,
                                        assetFileType:
                                            isUploadFromDeviceChecked,
                                    },
                                ];
                            }
                            return prevTextAssets; // Return the previous state if the asset already exists
                        });
                        break;
                    default:
                        break;
                }
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    function updateAsset(asset, key, value) {
        return { ...asset, [key]: value };
    }

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const removeTag = filterIdToRemove => {
        setFilters(currentFilters =>
            currentFilters.filter(filter => filter.id !== filterIdToRemove),
        );
    };

    const hasSelectedFilter = filterId =>
        filters.some(item => item.id === filterId);

    const loadAssets = async type => {
        setIsAssetsLoading(true);
        try {
            const baseAssetTypes = type === 'assetLink' ? [1, 2, 3] : [1, 2];
            let queryParams = {
                assetTypes: baseAssetTypes,
                page: currentPage,
                pageSize: 12,
            };

            const assetTypeMapping = {
                1: 1,
                2: 2,
                3: 3,
            };

            const fileTypeMapping = {
                5: ['mp4', 'mov'],
                6: ['mp3', 'wav'],
                7: ['jpg', 'jpeg', 'png', 'svg'],
                8: ['docx', 'doc', 'txt', 'pdf', 'ppt', 'pptx'],
                9: ['xls', 'xlsx', 'csv', 'numbers'],
            };

            const flags = {
                4: 'isShared',
                10: 'isAssetUploader',
                11: 'isSharedToMe',
                12: 'isSharedByMe',
                13: 'isGlobalAsset',
                14: 'isClientUploaded',
            };

            const filteredAssetTypes = Object.keys(assetTypeMapping)
                .filter(hasSelectedFilter)
                .map(id => assetTypeMapping[id]);
            const resolvedAssetTypes =
                filteredAssetTypes.length > 0
                    ? baseAssetTypes.filter(baseType =>
                          filteredAssetTypes.includes(baseType),
                      )
                    : baseAssetTypes;

            if (
                filteredAssetTypes.length > 0 &&
                resolvedAssetTypes.length === 0
            ) {
                setAssets([]);
                setTotalPages(1);
                return;
            }

            queryParams = {
                ...queryParams,
                assetTypes: resolvedAssetTypes,
            };

            const defaultFileTypeByPicker = {
                image: ['jpg', 'jpeg', 'png', 'svg'],
                video: ['mp4', 'mov'],
            };
            const typeFileTypes = defaultFileTypeByPicker[type] || [];
            const filteredFileTypes = Object.keys(fileTypeMapping)
                .filter(hasSelectedFilter)
                .flatMap(id => fileTypeMapping[id]);
            const resolvedFileTypes =
                filteredFileTypes.length > 0
                    ? typeFileTypes.length > 0
                        ? typeFileTypes.filter(fileType =>
                              filteredFileTypes.includes(fileType),
                          )
                        : filteredFileTypes
                    : typeFileTypes;

            if (
                filteredFileTypes.length > 0 &&
                resolvedFileTypes.length === 0
            ) {
                setAssets([]);
                setTotalPages(1);
                return;
            }

            queryParams = {
                ...queryParams,
                ...(resolvedFileTypes.length > 0
                    ? { fileTypes: resolvedFileTypes }
                    : {}),
            };

            Object.keys(flags).forEach(id => {
                if (hasSelectedFilter(id)) {
                    queryParams = { ...queryParams, [flags[id]]: true };
                }
            });
            if (searchTerm !== '' && searchTerm !== ' ') {
                queryParams = { ...queryParams, search: searchTerm };
            }
            const fetchedAssets = await fetchAssets(queryParams, null, false);
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        } finally {
            setIsAssetsLoading(false);
        }
    };

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(isAddFromAssetsChecked);
        }
    }, [currentPage, searchTerm, filters]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            setCurrentPage(1);
        }
    }, [searchTerm, filters, isAddFromAssetsChecked]);

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    // The asset list payload does not always carry a usable url, so resolve it from the asset record.
    const selectAssetLink = async pickedAsset => {
        let resolvedAsset = pickedAsset;

        try {
            const fetchedAsset = await fetchAssetByID(pickedAsset.id, null);
            resolvedAsset = {
                id: pickedAsset.id,
                name: fetchedAsset?.name || pickedAsset.name,
                url: fetchedAsset?.url || pickedAsset.url,
                fileType: fetchedAsset?.fileType || pickedAsset.fileType,
            };
        } catch (error) {
            console.error('Failed to fetch the asset link url', error);
        }

        if (!resolvedAsset.url) {
            Toast.error(getText('msg_cade149a85b2'));
            return false;
        }

        setSelectedAssetLink(resolvedAsset);
        setLinkDisplayText(prevText =>
            prevText && prevText.trim().length > 0
                ? prevText
                : resolvedAsset.name || '',
        );

        return true;
    };

    const addAsset = async () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];

        if (isAddFromAssetsChecked === 'assetLink') {
            const isAssetLinkSelected = await selectAssetLink(assetAdding);
            if (!isAssetLinkSelected) {
                return;
            }
        }

        if (['image', 'video'].includes(isAddFromAssetsChecked)) {
            setTextAssets(prevTextAssets => {
                const assetExists = prevTextAssets.some(
                    asset => asset.assetId === assetAdding.id,
                );
                if (assetExists) {
                    return prevTextAssets;
                }

                return [
                    ...prevTextAssets,
                    {
                        assetId: assetAdding.id,
                        assetFileType: isAddFromAssetsChecked,
                        url: assetAdding.url,
                    },
                ];
            });
        }

        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setFilters([]);
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    const enabledLinkTypes = getEnabledLinkTypes();
    const showLinkButton = enabledLinkTypes.length > 0;
    const hasActiveFilters = filters.length > 0;
    // The picker must outrank the link popover (1300) and any modal hosting the editor (1100).
    const isOverlayElevated =
        isAddFromAssetsChecked === 'assetLink' || disableAssetLink;

    const showTextMenu = features.fontStyling || features.clearFormatting;

    const showEditMenu = features.clipboard || features.history;

    const showTokenButton = features.token;

    const showAddMenu =
        showLinkButton ||
        features.phone ||
        features.image ||
        features.video ||
        features.qrCode ||
        showTokenButton;

    return (
        <div className="editor-container">
            <Popover
                open={isPopupOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: popoverPaperStyle,
                }}
                disablePortal={true}
                disableEnforceFocus={true}
                disableAutoFocus={true}
            >
                <div ref={popoverContentRef}>{popupContent}</div>
            </Popover>
            {isCellColorPopoverOpen && (
                <Popover
                    open={isCellColorPopoverOpen}
                    anchorEl={anchorCC}
                    onClose={() => {
                        setIsCellColorPopoverOpen(false);
                        setAnchorEl(null);
                        setIsPopupOpen(false);
                        setSelectedSubItem('');
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <div>
                        <ChromePicker
                            color={cellColor}
                            onChange={(color, event) => {
                                handleCellColorChange(color, event);
                            }}
                            disableAlpha={true}
                        />
                    </div>
                </Popover>
            )}
            <div className="menu-bar">
                <div className="rich-text-editor-menu-wrapper">
                    <div className="top-row">
                        {/* Text */}
                        {showTextMenu && (
                            <div
                                className={`icon toolbar-icon-spacing ${selectedItem === 'text' ? 'active' : ''}`}
                                onClick={event =>
                                    handleMenuItemClick(event, 'text')
                                }
                                onMouseOver={() => setShowTextTooltip(true)}
                                onMouseLeave={() => setShowTextTooltip(false)}
                            >
                                {showTextTooltip && (
                                    <div className={`hover-tag`}>
                                        {getText('msg_c3328c39b0e2')}
                                    </div>
                                )}
                                <i className="bi bi-type"></i>
                            </div>
                        )}

                        {/* Edit */}
                        {showEditMenu && (
                            <div
                                className={`icon toolbar-icon-spacing ${selectedItem === 'edit' ? 'active' : ''}`}
                                onClick={event =>
                                    handleMenuItemClick(event, 'edit')
                                }
                                onMouseOver={() => setShowEditTooltip(true)}
                                onMouseLeave={() => setShowEditTooltip(false)}
                            >
                                {showEditTooltip && (
                                    <div className={`hover-tag`}>
                                        {getText('msg_5301648dcf6b')}
                                    </div>
                                )}
                                <i className="bi bi-pencil"></i>
                            </div>
                        )}

                        {/* Paragraph */}
                        {features.paragraphStyling && (
                            <div
                                className={`icon toolbar-icon-spacing ${selectedItem === 'paragraph' ? 'active' : ''}`}
                                onClick={event =>
                                    handleMenuItemClick(event, 'paragraph')
                                }
                                onMouseOver={() =>
                                    setShowParagraphTooltip(true)
                                }
                                onMouseLeave={() =>
                                    setShowParagraphTooltip(false)
                                }
                            >
                                {showParagraphTooltip && (
                                    <div className={`hover-tag`}>
                                        {getText('msg_05058e0b6304')}
                                    </div>
                                )}
                                <i className="bi bi-paragraph"></i>
                            </div>
                        )}

                        {/* Emojis */}
                        {features.emoji && (
                            <>
                                <div className="emoji-picker-wrapper">
                                    <div
                                        className={`icon ${selectedItem === 'emoji' ? 'active-emoji' : ''}`}
                                        onClick={event =>
                                            handleMenuItemClick(event, 'emoji')
                                        }
                                        onMouseOver={() =>
                                            setShowEmojiTooltip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowEmojiTooltip(false)
                                        }
                                    >
                                        {showEmojiTooltip && (
                                            <div className={`hover-tag`}>
                                                {getText('msg_ac171aacfa24')}
                                            </div>
                                        )}
                                        <i className="bi bi-emoji-smile"></i>
                                    </div>
                                    {isEmojiPickerOpen && (
                                        <div className="color-picker-popover">
                                            <EmojiPicker
                                                onEmojiClick={handleEmojiSelect}
                                                skinTonesDisabled={true}
                                                previewConfig={previewConfig}
                                                categories={[
                                                    'smileys_people',
                                                    'animals_nature',
                                                    'food_drink',
                                                    'travel_places',
                                                    'activities',
                                                    'objects',
                                                    'symbols',
                                                    'flags',
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Add */}
                        {showAddMenu && (
                            <div
                                className={`icon toolbar-icon-spacing ${selectedItem === 'add' ? 'active' : ''}`}
                                onClick={event =>
                                    handleMenuItemClick(event, 'add')
                                }
                                onMouseOver={() => setShowAddTooltip(true)}
                                onMouseLeave={() => setShowAddTooltip(false)}
                            >
                                {showAddTooltip && (
                                    <div className={`hover-tag`}>
                                        {getText('msg_61cc55aa0453')}
                                    </div>
                                )}
                                <i className="bi bi-plus-lg"></i>
                            </div>
                        )}

                        {/* Table */}
                        {features.table && (
                            <div
                                className={`icon ${selectedItem === 'table' ? 'active' : ''}`}
                                onClick={event =>
                                    handleMenuItemClick(event, 'table')
                                }
                                onMouseOver={() => setShowTableTooltip(true)}
                                onMouseLeave={() => setShowTableTooltip(false)}
                            >
                                {showTableTooltip && (
                                    <div className={`hover-tag`}>
                                        {getText('msg_0424f6e7026f')}
                                    </div>
                                )}
                                <i className="bi bi-table"></i>
                            </div>
                        )}
                    </div>

                    {selectedItem && (
                        <div
                            className={`secondary-row${selectedItem === 'emoji' ? 'active-emoji' : ''}`}
                        >
                            {selectedItem === 'text' && (
                                <>
                                    {features.fontStyling && (
                                        <>
                                            <div
                                                className={`sub-icon ${editor.isActive('bold') ? 'active' : ''}`}
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .toggleBold()
                                                        .run()
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .toggleBold()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowTooltip(false)
                                                }
                                            >
                                                {showTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_19e07430eed6',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-type-bold"></i>
                                            </div>
                                            <div
                                                className={`sub-icon ${editor.isActive('italic') ? 'active' : ''}`}
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .toggleItalic()
                                                        .run()
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .toggleItalic()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowItalicTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowItalicTooltip(false)
                                                }
                                            >
                                                {showItalicTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_1616e2e5405e',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-type-italic"></i>
                                            </div>
                                            {/* Underline */}
                                            <div
                                                className={`sub-icon toolbar-icon-spacing ${editor.isActive('underline') ? 'active' : ''}`}
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .toggleUnderline()
                                                        .run()
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .toggleUnderline()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowUnderlineTooltip(
                                                        true,
                                                    )
                                                }
                                                onMouseLeave={() =>
                                                    setShowUnderlineTooltip(
                                                        false,
                                                    )
                                                }
                                            >
                                                {showUnderlineTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_39773aa3efa1',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-type-underline"></i>
                                            </div>
                                            <div
                                                className={`sub-icon toolbar-icon-spacing ${editor.isActive('strike') ? 'active' : ''}`}
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .toggleStrike()
                                                        .run()
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .toggleStrike()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowStrikeTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowStrikeTooltip(false)
                                                }
                                            >
                                                {showStrikeTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_a93b9a6808a9',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-type-strikethrough"></i>
                                            </div>
                                            <div
                                                className={`sub-icon toolbar-icon-spacing ${editor.isActive('highlight') ? 'active' : ''}`}
                                                onClick={
                                                    handleHighlightColorPickerToggle
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .toggleHighlight({
                                                            color: '#ffc078',
                                                        })
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowHighlightTooltip(
                                                        true,
                                                    )
                                                }
                                                onMouseLeave={() =>
                                                    setShowHighlightTooltip(
                                                        false,
                                                    )
                                                }
                                            >
                                                {showHighlightTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_e1da9275bc5b',
                                                        )}
                                                    </div>
                                                )}
                                                {displayHighlightColorPicker && (
                                                    <div className="color-picker-popover">
                                                        <div
                                                            className="color-picker-cover"
                                                            onClick={
                                                                handleHighlightColorPickerToggle
                                                            }
                                                        />
                                                        <ChromePicker
                                                            color={
                                                                highlightColor
                                                            }
                                                            onChange={(
                                                                color,
                                                                event,
                                                            ) =>
                                                                handleHighlightColorChange(
                                                                    color,
                                                                    event,
                                                                )
                                                            }
                                                            disableAlpha={true}
                                                        />
                                                    </div>
                                                )}
                                                <i className="bi bi-highlighter"></i>
                                            </div>
                                            <div>
                                                <div
                                                    className={`sub-icon ${editor.getAttributes('textStyle').color ? 'active' : ''}`}
                                                    onClick={
                                                        handleTextColorPickerToggle
                                                    }
                                                    disabled={
                                                        !editor
                                                            .can()
                                                            .chain()
                                                            .focus()
                                                            .setColor('#958DF1')
                                                            .run()
                                                    }
                                                    onMouseOver={() =>
                                                        setShowTextColorTooltip(
                                                            true,
                                                        )
                                                    }
                                                    onMouseLeave={() =>
                                                        setShowTextColorTooltip(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    {showTextColorTooltip && (
                                                        <div
                                                            className={`hover-tag`}
                                                        >
                                                            {getText(
                                                                'msg_3a53d53d14ee',
                                                            )}
                                                        </div>
                                                    )}
                                                    <i className="bi bi-paint-bucket"></i>
                                                </div>
                                                {displayTextColorPicker && (
                                                    <div className="color-picker-popover">
                                                        <div
                                                            className="color-picker-cover"
                                                            onClick={
                                                                handleTextColorPickerToggle
                                                            }
                                                        />
                                                        <ChromePicker
                                                            color={textColor}
                                                            onChange={(
                                                                color,
                                                                event,
                                                            ) =>
                                                                handleTextColorChange(
                                                                    color,
                                                                    event,
                                                                )
                                                            }
                                                            disableAlpha={true}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="font-size-dropdown">
                                                <Dropdown
                                                    defaultValue={
                                                        getDefaultFontSizeSize
                                                    }
                                                    options={fontSizeOptions}
                                                    onChange={option =>
                                                        handleFontSizeChange(
                                                            option,
                                                        )
                                                    }
                                                    useOnChangeValueFallback={
                                                        true
                                                    }
                                                    displayRelative={false}
                                                    width="max-content"
                                                />
                                            </div>
                                            <div className="font-family-dropdown">
                                                <Dropdown
                                                    allowSearch={true}
                                                    defaultValue={
                                                        editor.getAttributes(
                                                            'textStyle',
                                                        ).fontFamily ||
                                                        getText(
                                                            'msg_153188ed89eb',
                                                        )
                                                    }
                                                    options={[
                                                        {
                                                            label: getText(
                                                                'msg_153188ed89eb',
                                                            ),
                                                            value: 'Arial MS, Arial',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_14e5a68c0313',
                                                            ),
                                                            value: 'Brush Script MT',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_825c8005a4c8',
                                                            ),
                                                            value: 'Courier New MS, Courier New',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_9113c6c0c1f9',
                                                            ),
                                                            value: 'Georgia MS, Georgia',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_62036a7016ec',
                                                            ),
                                                            value: 'Impact MS, Impact',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_de5782693038',
                                                            ),
                                                            value: 'Inter',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_62f2e7343e7f',
                                                            ),
                                                            value: 'Tahoma MS, Tahoma',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_5843c571558e',
                                                            ),
                                                            value: 'Times New Roman MS, Times New Roman',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_55b017c05a83',
                                                            ),
                                                            value: 'Trebuchet MS, Trebuchet',
                                                        },
                                                        {
                                                            label: getText(
                                                                'msg_a9027ee708e8',
                                                            ),
                                                            value: 'Verdana MS, Verdana',
                                                        },
                                                    ]}
                                                    onChange={option =>
                                                        setFontStyle(option)
                                                    }
                                                />
                                            </div>
                                        </>
                                    )}
                                    {/* MARK ERASER */}
                                    {features.clearFormatting && (
                                        <div
                                            className="sub-icon toolbar-icon-spacing"
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .unsetAllMarks()
                                                    .run()
                                            }
                                            disabled={
                                                !editor
                                                    .can()
                                                    .chain()
                                                    .focus()
                                                    .unsetAllMarks()
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setshowEraserTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setshowEraserTooltip(false)
                                            }
                                        >
                                            {showEraserTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_24215bc6286f',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-eraser-fill"></i>
                                        </div>
                                    )}
                                </>
                            )}
                            {selectedItem === 'edit' && (
                                <>
                                    {features.clipboard && (
                                        <>
                                            {/* Copy (Clipboard) */}
                                            <div
                                                className="sub-icon toolbar-icon-spacing"
                                                onClick={handleCopy}
                                                onMouseOver={() =>
                                                    setShowCopyTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowCopyTooltip(false)
                                                }
                                            >
                                                {showCopyTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_af74f7c5362a',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-copy"></i>
                                            </div>

                                            {/* Paste (Clipboard) */}
                                            <div
                                                className="sub-icon toolbar-icon-spacing"
                                                onClick={handlePaste}
                                                onMouseOver={() =>
                                                    setShowPasteTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowPasteTooltip(false)
                                                }
                                            >
                                                {showPasteTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_db2483000d15',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-clipboard2-plus"></i>
                                            </div>

                                            {/* Cut (Scissors) */}
                                            <div
                                                className="sub-icon toolbar-icon-spacing"
                                                onClick={handleCut}
                                                onMouseOver={() =>
                                                    setShowCutTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowCutTooltip(false)
                                                }
                                            >
                                                {showCutTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_38d13bda5c40',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-scissors"></i>
                                            </div>
                                        </>
                                    )}

                                    {/* Undo (Arrow counterclockwise) */}
                                    {features.history && (
                                        <>
                                            <div
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .undo()
                                                        .run()
                                                }
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .undo()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowUndoTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowUndoTooltip(false)
                                                }
                                                className={classNames(
                                                    'sub-icon toolbar-icon-spacing',
                                                    {
                                                        'editor-action-disabled':
                                                            !editor
                                                                .can()
                                                                .chain()
                                                                .focus()
                                                                .undo()
                                                                .run(),
                                                    },
                                                )}
                                            >
                                                {showUndoTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_39fc72124884',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-arrow-counterclockwise"></i>
                                            </div>

                                            {/* Redo (Arrow clockwise) */}
                                            <div
                                                onClick={() =>
                                                    editor
                                                        .chain()
                                                        .focus()
                                                        .redo()
                                                        .run()
                                                }
                                                onMouseOver={() =>
                                                    setShowRedoTooltip(true)
                                                }
                                                onMouseLeave={() =>
                                                    setShowRedoTooltip(false)
                                                }
                                                className={classNames(
                                                    'sub-icon toolbar-icon-spacing',
                                                    {
                                                        'editor-action-disabled':
                                                            !editor
                                                                .can()
                                                                .chain()
                                                                .focus()
                                                                .undo()
                                                                .run(),
                                                    },
                                                )}
                                                disabled={
                                                    !editor
                                                        .can()
                                                        .chain()
                                                        .focus()
                                                        .redo()
                                                        .run()
                                                }
                                            >
                                                {showRedoTooltip && (
                                                    <div
                                                        className={`hover-tag`}
                                                    >
                                                        {getText(
                                                            'msg_471b94d402d1',
                                                        )}
                                                    </div>
                                                )}
                                                <i className="bi bi-arrow-clockwise"></i>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            {selectedItem === 'paragraph' &&
                                features.paragraphStyling && (
                                    <>
                                        <div
                                            className={`sub-icon ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .setTextAlign('left')
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowAlignLeftTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowAlignLeftTooltip(false)
                                            }
                                        >
                                            {showAlignLeftTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_9fb06a282f72',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-justify-left"></i>
                                        </div>

                                        <div
                                            className={`sub-icon ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .setTextAlign('center')
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowAlignCenterTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowAlignCenterTooltip(false)
                                            }
                                        >
                                            {showAlignCenterTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_472e0dd9e265',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-text-center"></i>
                                        </div>

                                        <div
                                            className={`sub-icon ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .setTextAlign('right')
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowAlignRightTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowAlignRightTooltip(false)
                                            }
                                        >
                                            {showAlignRightTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_2e324ba22982',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-justify-right"></i>
                                        </div>

                                        <div
                                            className={`sub-icon ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .setTextAlign('justify')
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowJustifyTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowJustifyTooltip(false)
                                            }
                                        >
                                            {showJustifyTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_536d2746f7f2',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-justify"></i>
                                        </div>

                                        <div
                                            className={`sub-icon ${editor.isActive('orderedList') ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .toggleOrderedList()
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowNumberedListTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowNumberedListTooltip(
                                                    false,
                                                )
                                            }
                                        >
                                            {showNumberedListTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_dd9dc343462e',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-list-ol"></i>
                                        </div>

                                        <div
                                            className={`sub-icon ${editor.isActive('bulletList') ? 'active' : ''}`}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .toggleBulletList()
                                                    .run()
                                            }
                                            onMouseOver={() =>
                                                setShowBulletedListTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowBulletedListTooltip(
                                                    false,
                                                )
                                            }
                                        >
                                            {showBulletedListTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_ce3a6efe2630',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-list-ul"></i>
                                        </div>
                                    </>
                                )}
                            {selectedItem === 'add' && (
                                <>
                                    {showLinkButton && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'link' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e => handleLinkClick(e)}
                                            onMouseOver={() =>
                                                setShowLinkTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowLinkTooltip(false)
                                            }
                                        >
                                            {showLinkTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_d0517071aa37',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-link"></i>
                                        </div>
                                    )}
                                    {features.phone && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'phone' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e => handlePhoneClick(e)}
                                            onMouseOver={() =>
                                                setShowPhoneTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowPhoneTooltip(false)
                                            }
                                        >
                                            {showPhoneTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_77064d526523',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-telephone-fill phone-icon"></i>
                                        </div>
                                    )}
                                    {features.image && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'image' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e =>
                                                handleButtonClick(e, 'image')
                                            }
                                            onMouseOver={() =>
                                                setShowImageTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowImageTooltip(false)
                                            }
                                        >
                                            {showImageTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_4426afd90a77',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-card-image"></i>
                                        </div>
                                    )}
                                    {features.video && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'video' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e =>
                                                handleButtonClick(e, 'video')
                                            }
                                            onMouseOver={() =>
                                                setShowVideoTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowVideoTooltip(false)
                                            }
                                        >
                                            {showVideoTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_bc17c1f0179a',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-play-btn"></i>
                                        </div>
                                    )}
                                    {features.qrCode && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'qr' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e =>
                                                handleButtonClick(e, 'qr')
                                            }
                                            onMouseOver={() =>
                                                setShowQrTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowQrTooltip(false)
                                            }
                                        >
                                            {showQrTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_44742187d5d4',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-qr-code"></i>
                                        </div>
                                    )}
                                    {showTokenButton && (
                                        <div
                                            className={`sub-icon ${selectedSubItem === 'token' ? 'active' : ''}`}
                                            variant="contained"
                                            onClick={e =>
                                                handleButtonClick(e, 'token')
                                            }
                                            onMouseOver={() =>
                                                setShowTokenTooltip(true)
                                            }
                                            onMouseLeave={() =>
                                                setShowTokenTooltip(false)
                                            }
                                        >
                                            {showTokenTooltip && (
                                                <div className={`hover-tag`}>
                                                    {getText(
                                                        'msg_a1141eb96836',
                                                    )}
                                                </div>
                                            )}
                                            <i className="bi bi-code"></i>
                                        </div>
                                    )}
                                </>
                            )}
                            {selectedItem === 'table' && features.table && (
                                <>
                                    <div
                                        className={`sub-svg toolbar-icon-spacing ${selectedSubItem === 'tableAdd' ? 'active' : ''}`}
                                        variant="contained"
                                        onClick={e => handleTableClick(e)}
                                        onMouseOver={() =>
                                            setShowInsertTableToolTip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowInsertTableToolTip(false)
                                        }
                                    >
                                        {showInsertTableTooltip && (
                                            <div className={`hover-tag`}>
                                                {getText('msg_95802daab3a2')}
                                            </div>
                                        )}
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 13"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M16.1053 0H1.78947C0.805263 0 0 0.835714 0 1.85714V3.71429V5.10714V7.89286V9.28571V11.1429C0 12.1643 0.805263 13 1.78947 13H16.1053C17.0895 13 17.8947 12.1643 17.8947 11.1429V9.28571V7.89286V5.10714V3.71429V1.85714C17.8947 0.835714 17.0895 0 16.1053 0ZM6.71053 7.89286V5.10714H11.1842V7.89286H6.71053ZM11.1842 9.28571V11.6071H6.71053V9.28571H11.1842ZM6.71053 3.71429V1.39286H11.1842V3.71429H6.71053ZM1.34211 1.85714C1.34211 1.57857 1.52105 1.39286 1.78947 1.39286H5.36842V3.71429H1.34211V1.85714ZM1.34211 5.10714H5.36842V7.89286H1.34211V5.10714ZM1.34211 11.1429V9.28571H5.36842V11.6071H1.78947C1.52105 11.6071 1.34211 11.4214 1.34211 11.1429ZM16.5526 11.1429C16.5526 11.4214 16.3737 11.6071 16.1053 11.6071H12.5263V9.28571H16.5526V11.1429ZM16.5526 7.89286H12.5263V5.10714H16.5526V7.89286ZM12.5263 3.71429V1.39286H16.1053C16.3737 1.39286 16.5526 1.57857 16.5526 1.85714V3.71429H12.5263Z"
                                                fill={
                                                    selectedSubItem ===
                                                    'tableAdd'
                                                        ? '#1366FF'
                                                        : 'black'
                                                }
                                            />
                                            <ellipse
                                                cx="16.3158"
                                                cy="9.20832"
                                                rx="3.68421"
                                                ry="3.79167"
                                                fill={
                                                    selectedSubItem ===
                                                    'tableAdd'
                                                        ? '#1366FF'
                                                        : 'black'
                                                }
                                            />
                                            <path
                                                d="M18.5263 9.53332H16.6315V11.4833H16V9.53332H14.1052V8.88332H16V6.93332H16.6315V8.88332H18.5263V9.53332Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                    <div
                                        className={`sub-svg toolbar-icon-spacing ${selectedSubItem === 'tableEdit' ? 'active' : ''}`}
                                        variant="contained"
                                        onClick={e => handleTableEditClick(e)}
                                        onMouseOver={() =>
                                            setShowEditTableTooltip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowEditTableTooltip(false)
                                        }
                                    >
                                        {showEditTableTooltip && (
                                            <div className={`hover-tag`}>
                                                {getText('msg_5301648dcf6b')}
                                            </div>
                                        )}
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 13"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M16.1053 0H1.78947C0.805263 0 0 0.835714 0 1.85714V3.71429V5.10714V7.89286V9.28571V11.1429C0 12.1643 0.805263 13 1.78947 13H16.1053C17.0895 13 17.8947 12.1643 17.8947 11.1429V9.28571V7.89286V5.10714V3.71429V1.85714C17.8947 0.835714 17.0895 0 16.1053 0ZM6.71053 7.89286V5.10714H11.1842V7.89286H6.71053ZM11.1842 9.28571V11.6071H6.71053V9.28571H11.1842ZM6.71053 3.71429V1.39286H11.1842V3.71429H6.71053ZM1.34211 1.85714C1.34211 1.57857 1.52105 1.39286 1.78947 1.39286H5.36842V3.71429H1.34211V1.85714ZM1.34211 5.10714H5.36842V7.89286H1.34211V5.10714ZM1.34211 11.1429V9.28571H5.36842V11.6071H1.78947C1.52105 11.6071 1.34211 11.4214 1.34211 11.1429ZM16.5526 11.1429C16.5526 11.4214 16.3737 11.6071 16.1053 11.6071H12.5263V9.28571H16.5526V11.1429ZM16.5526 7.89286H12.5263V5.10714H16.5526V7.89286ZM12.5263 3.71429V1.39286H16.1053C16.3737 1.39286 16.5526 1.57857 16.5526 1.85714V3.71429H12.5263Z"
                                                fill={
                                                    selectedSubItem ===
                                                    'tableEdit'
                                                        ? '#1366FF'
                                                        : 'black'
                                                }
                                            />
                                            <ellipse
                                                cx="16.3158"
                                                cy="9.20832"
                                                rx="3.68421"
                                                ry="3.79167"
                                                fill={
                                                    selectedSubItem ===
                                                    'tableEdit'
                                                        ? '#1366FF'
                                                        : 'black'
                                                }
                                            />
                                            <path
                                                d="M16.6027 7.87157C16.6906 7.76505 16.6754 7.60748 16.5689 7.51963C16.4624 7.43178 16.3048 7.44691 16.217 7.55343L16.6027 7.87157ZM14.6729 9.81852L14.86 9.98454L14.8658 9.97758L14.6729 9.81852ZM14.6225 9.93814L14.373 9.9214L14.3728 9.92624L14.6225 9.93814ZM14.5813 10.8033L14.3316 10.7914C14.331 10.8041 14.3313 10.8169 14.3327 10.8297L14.5813 10.8033ZM14.8131 10.9999L14.821 11.2498C14.8372 11.2492 14.8534 11.2471 14.8692 11.2435L14.8131 10.9999ZM15.6966 10.7964L15.7527 11.0401L15.7565 11.0391L15.6966 10.7964ZM15.81 10.7266L16.0006 10.8884L16.0028 10.8857L15.81 10.7266ZM17.7816 8.73017C17.8695 8.62367 17.8544 8.4661 17.7479 8.37822C17.6414 8.29034 17.4838 8.30543 17.3959 8.41192L17.7816 8.73017ZM16.218 7.55339C16.1301 7.65988 16.1452 7.81745 16.2516 7.90534C16.3581 7.99323 16.5157 7.97815 16.6036 7.87166L16.218 7.55339ZM14.4859 9.6526C14.4199 9.72697 14.3798 9.82126 14.3731 9.9214L14.872 9.95488C14.8712 9.96675 14.8664 9.97705 14.8599 9.98443L14.4859 9.6526ZM14.3728 9.92624L14.3316 10.7914L14.831 10.8152L14.8722 9.95004L14.3728 9.92624ZM14.3327 10.8297C14.3593 11.0802 14.5778 11.2575 14.821 11.2498L14.8051 10.75C14.8093 10.7499 14.8145 10.7514 14.8191 10.7553C14.8237 10.7592 14.8288 10.7668 14.8299 10.7769L14.3327 10.8297ZM14.8692 11.2435L15.7527 11.04L15.6405 10.5528L14.757 10.7563L14.8692 11.2435ZM15.7565 11.0391C15.8511 11.0158 15.9368 10.9634 16.0005 10.8884L15.6194 10.5648C15.6247 10.5585 15.631 10.5551 15.6367 10.5537L15.7565 11.0391ZM16.0028 10.8857L17.7816 8.73017L17.3959 8.41192L15.6171 10.5675L16.0028 10.8857ZM16.6036 7.87166L17.0713 7.30502L16.6856 6.98675L16.218 7.55339L16.6036 7.87166ZM17.081 7.29246C17.1115 7.25028 17.1762 7.2355 17.226 7.26652L17.4903 6.84209C17.2215 6.67466 16.8632 6.74063 16.6759 6.99931L17.081 7.29246ZM17.201 7.24868L17.8598 7.78155L18.1742 7.3928L17.5154 6.85993L17.201 7.24868ZM17.8426 7.76631C17.8587 7.782 17.8663 7.80172 17.8662 7.82069L18.3662 7.82351C18.3671 7.66641 18.3033 7.51698 18.1914 7.40805L17.8426 7.76631ZM17.8662 7.82069C17.8661 7.83966 17.8583 7.85935 17.8419 7.87491L18.1868 8.23698C18.2998 8.1293 18.3653 7.98061 18.3662 7.82351L17.8662 7.82069ZM17.8216 7.89672L17.396 8.41185L17.7815 8.7303L18.2071 8.21517L17.8216 7.89672ZM16.1637 7.7506C16.2703 8.4422 16.9248 8.91089 17.6215 8.81889L17.5561 8.3232C17.1164 8.38125 16.7212 8.08495 16.6579 7.6744L16.1637 7.7506Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                    <div
                                        className={`sub-svg toolbar-icon-spacing ${selectedSubItem === 'tableCell' ? 'active' : ''}`}
                                        onClick={e => handleTableCellClick(e)}
                                        onMouseOver={() =>
                                            setShowCellTableTooltip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowCellTableTooltip(false)
                                        }
                                    >
                                        {showCellTableTooltip && (
                                            <div className={`hover-tag`}>
                                                {getText('msg_783d66a925a8')}
                                            </div>
                                        )}
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 18 13"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M1.78947 0H16.1053C17.0895 0 17.8947 0.835714 17.8947 1.85714V3.71429V5.10714V7.89286V9.28571V11.1429C17.8947 12.1643 17.0895 13 16.1053 13H1.78947C0.805263 13 0 12.1643 0 11.1429V9.28571V7.89286V5.10714V3.71429V1.85714C0 0.835714 0.805263 0 1.78947 0ZM11.1842 11.6071V9.28571H6.71053V11.6071H11.1842ZM6.71053 1.39286V3.71429H11.1842V1.39286H6.71053ZM1.78947 1.39286C1.52105 1.39286 1.34211 1.57857 1.34211 1.85714V3.71429H5.36842V1.39286H1.78947ZM5.36842 5.10714H1.34211V7.89286H5.36842V5.10714ZM1.34211 9.28571V11.1429C1.34211 11.4214 1.52105 11.6071 1.78947 11.6071H5.36842V9.28571H1.34211ZM16.1053 11.6071C16.3737 11.6071 16.5526 11.4214 16.5526 11.1429V9.28571H12.5263V11.6071H16.1053ZM12.5263 7.89286H16.5526V5.10714H12.5263V7.89286ZM12.5263 3.71429V1.39286H16.5526V1.85714C16.5526 1.57857 16.3737 1.39286 16.1053 1.39286H12.5263Z"
                                                fill={
                                                    selectedSubItem ===
                                                    'tableCell'
                                                        ? '#1366FF'
                                                        : 'black'
                                                }
                                            />
                                        </svg>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <EditorContent
                editor={editor}
                className="custom-editor disable-links"
            />
            {/* Upload From Device */}
            {isUploadFromDeviceChecked &&
                ReactDOM.createPortal(
                    <div className="overlay-background">
                        <div className="overlay-container card shadow-sm border-light">
                            <p className="overlay-title">
                                {getText('msg_0625a2d6e606')}
                            </p>
                            <div className="upload-file-drop">
                                <FileDrop
                                    file={file}
                                    onChange={handleFileChange}
                                />
                            </div>

                            <InputField
                                label={getText('msg_709a23220f2c')}
                                onChange={value =>
                                    setAsset(updateAsset(asset, 'name', value))
                                }
                                maxChars={256}
                                required={true}
                                placeholder={getText('msg_b7ffb9789f09')}
                                value={asset.name}
                            />

                            <div className="overlay-section-margin">
                                <div className="file-form-label">
                                    {getText('msg_848eed0fbd54')}
                                    <Tooltip
                                        html={getText('msg_79449ece0a91')}
                                        placement="right"
                                    >
                                        <i className="bi bi-info-circle overlay-info-icon" />
                                    </Tooltip>
                                </div>
                                <TagInput
                                    onTagsChange={tags =>
                                        setAsset(
                                            updateAsset(
                                                asset,
                                                'assetTags',
                                                tags,
                                            ),
                                        )
                                    }
                                    savedTags={asset.tags}
                                />
                            </div>

                            <div className="overlay-section-margin">
                                <SwitchField
                                    label={getText('msg_0c359a10ae17')}
                                    onChange={isChecked =>
                                        setAsset(
                                            updateAsset(
                                                asset,
                                                'assetTypeId',
                                                isChecked ? 1 : 2,
                                            ),
                                        )
                                    }
                                    tooltip={getText('msg_77453f9dd036')}
                                    checked={asset.assetTypeId !== 2}
                                />
                            </div>

                            <div
                                id="footer"
                                className={classNames('overlay-footer', {
                                    tabPanelHidden: !isUploadFromDeviceChecked,
                                })}
                            >
                                <div
                                    type="button"
                                    className="float-start button-base button-secondary overlay-footer-button"
                                    onClick={() =>
                                        setIsUploadFromDeviceChecked('')
                                    }
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </div>
                                <div
                                    type="button"
                                    className="float-end button-base ing-button-primary-left overlay-footer-button"
                                    onClick={() => uploadAsset()}
                                >
                                    {getText('msg_61cc55aa0453')}
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked &&
                ReactDOM.createPortal(
                    <div
                        style={{
                            ...overlayBackground,
                            // Must render above the link Popover (zIndex 1300) when picking an asset link
                            zIndex:
                                isAddFromAssetsChecked === 'assetLink'
                                    ? 1500
                                    : 1002,
                        }}
                    >
                        <div
                            style={{
                                ...overlayContainer,
                                zIndex:
                                    isAddFromAssetsChecked === 'assetLink'
                                        ? 1501
                                        : 1003,
                            }}
                            className="card shadow-sm border-light"
                        >
                            <div className="asset-picker-search-row">
                                <div
                                    className={`${style.searchWrapper} asset-picker-search-wrapper`}
                                >
                                    <input
                                        type="text"
                                        className={`${style.searchInput} asset-picker-search-input`}
                                        placeholder={getText(
                                            'msg_b18cdf8f03e9',
                                        )}
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <span className={style.searchIcon}>
                                        <i className="bi bi-search"></i>
                                    </span>
                                </div>

                                <div className="asset-picker-filter-dropdown">
                                    <SearchableDropdown
                                        options={FILTER_OPTIONS_ASSET_LIBRARY}
                                        optionIDField="id"
                                        savedOptions={filters}
                                        savedOptionIDField="id"
                                        updateOptions={setFilters}
                                        disabled={false}
                                        buttonMode={true}
                                        buttonLabel={getText(
                                            'msg_d7decf1aa22b',
                                        )}
                                        buttonIcon={'bi bi-filter-circle'}
                                        showTags={false}
                                    />
                                </div>
                            </div>

                            {hasActiveFilters && (
                                <div className={style.filterTagsWrap}>
                                    {filters.map(filter => (
                                        <div
                                            key={filter.id}
                                            className={`${style.filterTag} ${style.filterTagNoOffset}`}
                                        >
                                            <i
                                                className="bi bi-x"
                                                onClick={() =>
                                                    removeTag(filter.id)
                                                }
                                            />
                                            <span>{filter.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <AssetGridView
                                assets={assets}
                                propertiesModel={true}
                                handleClick={handleClick}
                                handleDoubleClick={() => null}
                                activeCard={activeCard}
                                fullAssetName={false}
                            />

                            {assets.length < 1 && (
                                <NotFoundBox
                                    backgroundColor="#f0f4ff"
                                    title={
                                        searchTerm || hasActiveFilters
                                            ? getText('msg_aca14ec90fb5')
                                            : getText('msg_e1f2a3b4c5d6')
                                    }
                                    description={
                                        searchTerm || hasActiveFilters
                                            ? getText('msg_12987c465e21')
                                            : getText('msg_9a2c7ef03b6d')
                                    }
                                />
                            )}

                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />

                            <div
                                id="footer"
                                className={classNames('overlay-footer', {
                                    tabPanelHidden: !isAddFromAssetsChecked,
                                })}
                            >
                                <button
                                    type="button"
                                    className="float-start button-base button-secondary overlay-footer-button"
                                    onClick={() => {
                                        setIsAddFromAssetsChecked('');
                                        setFilters([]);
                                    }}
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </button>
                                <div
                                    type="button"
                                    className="float-end button-base ing-button-primary-left overlay-footer-button"
                                    onClick={() => addAsset()}
                                >
                                    {getText('msg_61cc55aa0453')}
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default RichTextEditor;
